// Arrangement playback.
//
// Two callers with different audio available, one engine:
//
//  • A ROOM has per-clip audio — every canonical clip carries an R2 `audioKey`
//    and a beat position, so clips are scheduled individually on the timeline.
//  • A SEED does not. Its snapshot clips reference `filePath`, an absolute path
//    on the producer's own machine, which means nothing here. What a seed does
//    have is one full-length rendered stem per track, so each lane gets a single
//    source pinned at 0 — which reproduces the same arrangement.
//
// Everything is scheduled ahead of time on the AudioContext clock rather than
// driven from React. `setTimeout` would drift audibly within a bar; the clock
// does not, and it is the only thing that keeps clips aligned to each other.

import { CENTER_PAN, UNITY_VOLUME, clampPan, clampVolume, faderToGain, isAudible, panGains, sendToGain } from "@shared/mixer.js";
import { decodeAudio } from "@shared/audio/master-mix.js";
import { beatUnit, beatsPerBar, FOUR_FOUR, type Meter } from "@web-shared/lib/meter";
import { DEFAULT_PATCH, type SynthPatch } from "@web-shared/lib/synth";
import { scheduleTone, stopVoice } from "@web-shared/lib/synth-voice";
import { CALIBRATION_CLICKS, CALIBRATION_SECONDS, estimateLatency, type LatencyEstimate } from "@web-shared/lib/latency";
import { buildGraph, stagesFor, type DeviceGraph, type Stage } from "@web-shared/lib/device-graph";
import { WARP_MODE_REPITCH, type WarpMarker } from "@web-shared/lib/warp";
import { isIdentity, renderWarped, warpModeOf, type RenderSpec } from "@web-shared/lib/stretch/render";
import { defaultRenderer, Superseded, type StretchRenderer } from "@web-shared/lib/stretch/client";
import type { Onset } from "@web-shared/lib/stretch/onsets";
import { envelope, normalGain, summarize, type Channels, type WaveSummary } from "@web-shared/lib/waveform";
import type { DeviceState } from "@shared/sync/state.js";
import { audioFetch } from "@web-shared/lib/api";
import { warpSettingsKey, type WarpSettingsData } from "@shared/sync/protocol.js";

/** Ramp at each clip voice's edges so a cut in the middle of a waveform
 * doesn't click. Short enough to be inaudible as a fade. */
const DECLICK_SEC = 0.004;

export interface ClipSource {
  id: string;
  /** Which lane's gain node this plays through — drives mute/solo. */
  laneId: string;
  /** Tokenized URL of the audio. Deduped, so a reused clip is fetched once. */
  url: string;
  /** Position on the timeline, in seconds. */
  startSec: number;
  /**
   * How long to play from the timeline position. Omit for "however long the
   * file is" — a seed's stems are full-length track renders whose extent isn't
   * known until they decode.
   */
  durationSec?: number;
  /** Where in the source file to begin. */
  offsetSec?: number;
  /** A launched clip's loop brace in OBJECT seconds: play from `offsetSec`
   * to `loopEndSec`, then repeat [loopStartSec, loopEndSec) — Live's
   * Session View behaviour for a warped, looping clip. Absent ⇒ the whole
   * object loops. */
  loopStartSec?: number;
  loopEndSec?: number;
  /**
   * A warped clip whose object is at another tempo (slice 48): the speed
   * factor room tempo ÷ source tempo, so the object's beats land on the
   * room's grid. `offsetSec`, `loopStartSec` and `loopEndSec` stay OBJECT
   * seconds; `durationSec` stays room seconds. Absent or 1 ⇒ the object
   * plays at its own speed.
   */
  tempoRatio?: number;
  /** Live's Re-Pitch warp mode: the speed change moves the pitch (a
   * playback rate) instead of a stretch that keeps it. */
  repitch?: boolean;
  /** Live's warp mode value when it is not Re-Pitch and not Complex (the
   * default): Beats, Complex Pro… — which algorithm renders the stretch. */
  warpMode?: number;
  /** The mode's knobs (Beats, Complex Pro), when the clip set any. */
  warpSettings?: WarpSettingsData;
  /** The room's tempo, for Beats' Preserve divisions. */
  gridBpm?: number;
  /**
   * Live's warp markers (slice 49): the object is played through them onto
   * a grid at `bpm` (a stretched copy per object, tempo and map), and
   * `offsetSec` / `loopStartSec` / `loopEndSec` are then ROOM seconds on
   * that grid. Overrides `tempoRatio`.
   */
  warpMap?: { markers: readonly WarpMarker[]; bpm: number; fallbackBpm: number };
  /** The clip's Transpose + Detune, in semitones. */
  semitones?: number;
  /** Play `semitones` as a playback rate (an unwarped clip: speed and pitch
   * together, as Live does) rather than a pitch shift at a kept length. */
  pitchByRate?: boolean;
}

/** One clip as the context will play it: which buffer (the object, or its
 * stretched copy), where in that buffer to start, how much of it to play
 * (BUFFER seconds — what `start(when, offset, duration)` takes) and at
 * what rate; `outSec` is how long that lasts on the timeline. */
interface Resolved {
  buffer: AudioBuffer;
  offset: number;
  playFor: number;
  rate: number;
  outSec: number;
  /** Object seconds → buffer seconds (a stretched copy at a constant ratio). */
  scale: number;
}

/** One MIDI note, resolved to seconds relative to its clip's start. */
export interface MidiNoteEvent {
  /** MIDI pitch, 0–127 (69 = A4). */
  pitch: number;
  startSec: number;
  durationSec: number;
  /** 0–127; scales the tone's level. */
  velocity: number;
}

/**
 * A MIDI clip on the timeline. Nothing to fetch or decode — the notes ARE the
 * content, and the engine synthesises them (a plain sine per note, for now).
 */
export interface MidiSource {
  id: string;
  /** Which lane's gain node this plays through — drives mute/solo. */
  laneId: string;
  /** Position on the timeline, in seconds. */
  startSec: number;
  durationSec: number;
  notes: MidiNoteEvent[];
  /** Roll the notes' chance and velocity deviation afresh — a launched
   * clip calls it once per pass, so a 50% note plays on half the passes
   * as Live's does. Absent when no note has either. */
  reroll?: () => MidiNoteEvent[];
  /** What the source is MADE from (the wire notes and the lane's effects),
   * so a rebuild that merely rolled chance again is not a change worth a
   * reschedule (the hook's signature reads this over the rolled notes). */
  key?: string;
}

/**
 * Everything about a source that changes what it sounds like, so an edit to
 * any of it (a warp marker dragged, a transpose, Re-Pitch toggled, a loop
 * brace moved) reschedules the transport, swaps a launched pad, restarts a
 * preview. Placement alone is not enough: the clip editor changes how a clip
 * plays without moving it.
 */
export function sourceSignature(s: ClipSource): string {
  const warp = s.warpMap
    ? `m${s.warpMap.bpm.toFixed(3)}/${s.warpMap.fallbackBpm.toFixed(3)}/${s.warpMap.markers.map((m) => `${m.beat.toFixed(4)}@${m.sec.toFixed(4)}`).join(",")}`
    : `r${(s.tempoRatio ?? 1).toFixed(5)}`;
  return [
    s.id,
    s.url,
    s.startSec,
    s.durationSec ?? "*",
    s.offsetSec ?? 0,
    s.loopStartSec ?? "",
    s.loopEndSec ?? "",
    warp,
    s.repitch ? "p" : s.warpMode !== undefined ? `w${s.warpMode}` : "",
    warpSettingsKey(s.warpSettings),
    (s.semitones ?? 0).toFixed(3),
    s.pitchByRate ? "v" : "",
  ].join(":");
}

/** The same for either kind: a MIDI source by what it was MADE from when it
 * says (a rebuild that rolled chance again is the same material), else by
 * its note content. */
export function playbackSignature(src: ClipSource | MidiSource): string {
  if (!("notes" in src)) return sourceSignature(src);
  return `${src.id}:${src.startSec}:${src.durationSec}:${
    src.key ?? src.notes.map((n) => `${n.pitch}@${n.startSec.toFixed(4)}+${n.durationSec.toFixed(4)}v${n.velocity}`).join(",")
  }`;
}

export interface LoadProgress {
  loaded: number;
  total: number;
  bytes: number;
  failed: number;
}

export type PlayerStatus = "idle" | "loading" | "ready" | "playing";

interface Voice {
  node: AudioBufferSourceNode | OscillatorNode;
}

/** A looping MIDI clip being laid a few passes ahead of the clock
 * (`ArrangementPlayer.feedMidi`). */
interface MidiFeed {
  src: MidiSource;
  out: AudioNode;
  patch: SynthPatch;
  /** Context time pass 0 starts at. */
  base: number;
  period: number;
  /** Notes before this, in pass 0, are skipped — a swap picking up mid-pass. */
  phase: number;
  passes: number;
  /** The next pass to lay. */
  next: number;
  /** The owner's voice list; laid voices go in, ended ones are pruned. */
  nodes: Array<AudioBufferSourceNode | OscillatorNode>;
  last: OscillatorNode | null;
  lastEnd: number;
  /** Whether the owner still wants passes laid. */
  live: () => boolean;
  /** Every pass laid: the voice that ends last, if any sounds at all. */
  done: (last: OscillatorNode | null) => void;
}

/** Loading every stem of a long arrangement is a real download; warn past this. */
export const LARGE_LOAD_BYTES = 120 * 1024 * 1024;

/** The mixer position of one lane, as the Set holds it (shared/mixer.ts). */
export interface LaneLevel {
  /** Fader position, 0..1. */
  volume: number;
  /** Pan position, -1..1. */
  pan: number;
}

/** Per-lane node chain: mute/solo gate → devices → fader level → pan → master. */
interface LaneNodes {
  /** Where the lane's TIMELINE material enters, ahead of the gate: closed
   * while a Session launch overrides the lane (Live's rule — a track plays
   * its Session clip or its arrangement, never both). Launched voices join
   * at the gate and never pass through it. */
  arrangement: GainNode;
  gate: GainNode;
  /** The lane's device chain, approximated (device-graph.ts) — pre-fader,
   * as Live's is. Rebuilt whenever the chain or the hearing preference
   * changes; a straight wire when there is nothing to model. */
  chain: DeviceGraph;
  level: GainNode;
  pan: Panner;
  /** The lane's own path to the master, after the send taps: a soloed return
   * closes it (Live's solo is global — only the return's wet signal is heard)
   * while the sends keep feeding that return. */
  direct: GainNode;
  /** Post-fader tap for the mixer's meter. */
  meter: AnalyserNode;
}

/** Waveform overviews kept decoded; past this the oldest are dropped. */
const PEAK_CACHE_MAX = 512;
/** Object summaries kept for waveforms (~270 KB for 3 minutes of audio). */
const SUMMARY_CACHE_MAX = 256;
/** Objects fetched at once to draw them. */
const PEAK_FETCH_CONCURRENCY = 6;
/** Objects a load or reload fetches at once: enough that one decodes while
 * the next downloads, few enough not to split the bandwidth many ways. */
const LOAD_CONCURRENCY = 3;
/** How long the waveform queue must stay empty before `prefetch` starts. */
const PEAKS_SETTLE_MS = 250;
/** Play starts once what sounds this far ahead has decoded (`loadForPlay`). */
export const PLAY_READY_SEC = 10;

/** `job`, unless `signal` aborts first — the caller stops waiting; the job runs on. */
function untilAborted<T>(job: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(signal.reason);
    signal.addEventListener("abort", onAbort, { once: true });
    job.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

function channelsOf(buffer: AudioBuffer): Channels {
  return {
    sampleRate: buffer.sampleRate,
    length: buffer.length,
    channels: Array.from({ length: buffer.numberOfChannels }, (_, c) => buffer.getChannelData(c)),
  };
}

/**
 * Decode an object with the context that plays it. AIFF goes through the
 * pipeline's own reader: Chrome's decoder refuses it, and jam renders from
 * Live shipped AIFF under `.wav` keys until 2026-10-12 — those seeds still
 * hold it. Everything else is the browser's.
 */
export async function decodeObject(ctx: BaseAudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  if (!isAiff(bytes)) return ctx.decodeAudioData(bytes);
  const a = decodeAudio(new Uint8Array(bytes));
  if (!a) throw new Error("Could not decode the clip (unsupported AIFF).");
  const buffer = ctx.createBuffer(a.channels, Math.max(1, a.frames), a.sampleRate);
  for (let c = 0; c < a.channels; c++) {
    const out = buffer.getChannelData(c);
    for (let f = 0; f < a.frames; f++) out[f] = a.samples[f * a.channels + c]!;
  }
  return buffer;
}

function isAiff(bytes: ArrayBuffer): boolean {
  if (bytes.byteLength < 12) return false;
  const tag = (o: number) => String.fromCharCode(...new Uint8Array(bytes, o, 4));
  return tag(0) === "FORM" && (tag(8) === "AIFF" || tag(8) === "AIFC");
}

/** Time-domain window a meter reads; ~21 ms at 48 kHz, enough for a peak. */
const METER_WINDOW = 1024;

export interface SceneLaunchResult {
  /** Clips in the row that actually sounded. */
  started: number;
  /** Clips the row named whose object would not load. */
  failed: number;
  /** The beat the row lands on — ONE beat for the whole row, which is what
   * a session recording spans every lane of it from. Null when nothing
   * sounded, or when the transport is not running to have a beat. */
  atBeat: number | null;
  /** The lanes that took a clip, and which. */
  launched: Array<{ laneId: string; clipId: string }>;
}

/** How a browser-style preview should play. */
/** How far ahead an audition is placed: enough for its first attack to be
 * built before the clock gets there. */
const AUDITION_LEAD_SEC = 0.02;
/** How far ahead the transport is placed when it starts. Scheduling a whole
 * arrangement — every synth note is a dozen nodes — takes the main thread
 * tens of milliseconds on a dense Set; anchored at "now", the voices built
 * last were already late, and MIDI flammed behind the audio on every play,
 * seek and loop wrap (playback-audit §1.2). Applied BEFORE quantization, so
 * a start on a bar line is still on that line. */
export const TRANSPORT_LEAD_SEC = 0.05;
/** An edit while playing: the old schedule fades out as the new one fades
 * in, over this long, both at the playhead — a change of pitch or warp is
 * heard as a change, never as the clip starting over. */
export const RESCHEDULE_XFADE_SEC = 0.03;

export interface AuditionOptions {
  /** Start on the next bar line while the transport or the Session clock
   * runs — Live's default preview; off is Live's "Raw". */
  sync?: boolean;
  /** Keep looping the clip's region until the next preview or a stop. */
  loop?: boolean;
  /** Play the clip at a matched level (its region's RMS brought to
   * `AUDITION_LEVEL_RMS`), so a quiet field recording and a slammed loop
   * preview at one loudness. Bounded: never more than 12 dB either way. */
  level?: boolean;
}

/** The RMS an audition is matched to: about -18 dBFS, a mix bus's comfort. */
const AUDITION_LEVEL_RMS = 0.125;
const AUDITION_LEVEL_MAX_GAIN = 4;

export class ArrangementPlayer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** The Set's master fader + pan, applied after every lane and before the
   * limiter — the mix the Set makes, heard. Playback volume (`setVolume`)
   * is a listening control on top. */
  private masterFader: { volume: number; pan: number } = { volume: UNITY_VOLUME, pan: CENTER_PAN };
  private masterFaderNodes: { level: GainNode; pan: Panner; chain: DeviceGraph } | null = null;
  // ---- device chains, heard approximately ---------------------------------
  // The Set's chains as stages (device-graph.ts); one flag turns the whole
  // interpretation off (every graph becomes a straight wire).
  private hearDevices = true;
  private laneStages = new Map<string, Stage[]>();
  private masterStages: Stage[] = [];
  private returnStages: Stage[][] = [];

  private stagesOf(stages: Stage[] | undefined): Stage[] {
    return this.hearDevices ? (stages ?? []) : [];
  }

  /** Hear the device chains (approximately) or play everything dry. */
  setHearDevices(on: boolean): void {
    if (this.hearDevices === on) return;
    this.hearDevices = on;
    if (!this.ctx) return;
    for (const laneId of this.lanes.keys()) this.rebuildLaneChain(laneId);
    this.rebuildMasterChain();
    this.returnBuses.forEach((_, i) => this.rebuildReturnChain(i));
  }

  // The chains as given, so a tempo change can re-derive the stages.
  private laneChains = new Map<string, readonly DeviceState[] | undefined>();
  private masterChain: readonly DeviceState[] | undefined;
  private returnChains: Array<readonly DeviceState[] | undefined> = [];

  private restageChains(): void {
    for (const [laneId, devices] of this.laneChains) this.setLaneDevices(laneId, devices);
    this.setMasterDevices(this.masterChain);
    this.returnChains.forEach((devices, i) => this.setReturnDevices(i, devices));
  }

  /** The lane's chain as the room holds it (absent ⇒ unknown, heard dry). */
  setLaneDevices(laneId: string, devices: readonly DeviceState[] | undefined): void {
    this.laneChains.set(laneId, devices);
    const stages = stagesFor(devices, this.bpm);
    const prev = this.laneStages.get(laneId);
    if (prev && JSON.stringify(prev) === JSON.stringify(stages)) return;
    this.laneStages.set(laneId, stages);
    if (this.ctx && this.lanes.has(laneId)) this.rebuildLaneChain(laneId);
  }

  setMasterDevices(devices: readonly DeviceState[] | undefined): void {
    this.masterChain = devices;
    const stages = stagesFor(devices, this.bpm);
    if (JSON.stringify(this.masterStages) === JSON.stringify(stages)) return;
    this.masterStages = stages;
    if (this.ctx) this.rebuildMasterChain();
  }

  setReturnDevices(index: number, devices: readonly DeviceState[] | undefined): void {
    this.returnChains[index] = devices;
    const stages = stagesFor(devices, this.bpm);
    if (JSON.stringify(this.returnStages[index] ?? []) === JSON.stringify(stages)) return;
    this.returnStages[index] = stages;
    if (this.ctx && this.returnBuses[index]) this.rebuildReturnChain(index);
  }

  /** Swap a chain graph between `from` and `to`, disposing the old one. */
  private swapChain(from: AudioNode, old: DeviceGraph | null, to: AudioNode, stages: Stage[]): DeviceGraph {
    const ctx = this.ensureContext();
    // Same shape: move the knobs in place — a tail keeps ringing, a
    // compressor keeps its state — and nothing is torn down.
    if (old && old.update(stages)) return old;
    if (old) {
      try {
        from.disconnect(old.input);
      } catch {
        // never connected
      }
      old.dispose();
    }
    const next = buildGraph(ctx, stages);
    from.connect(next.input);
    next.output.connect(to);
    return next;
  }

  private rebuildLaneChain(laneId: string): void {
    const nodes = this.lanes.get(laneId);
    if (!nodes) return;
    nodes.chain = this.swapChain(nodes.gate, nodes.chain, nodes.level, this.stagesOf(this.laneStages.get(laneId)));
  }

  private rebuildMasterChain(): void {
    if (!this.master || !this.masterFaderNodes) return;
    this.masterFaderNodes.chain = this.swapChain(this.master, this.masterFaderNodes.chain, this.masterFaderNodes.level, this.stagesOf(this.masterStages));
  }

  private rebuildReturnChain(index: number): void {
    const bus = this.returnBuses[index];
    if (!bus) return;
    bus.chain = this.swapChain(bus.input, bus.chain, bus.level, this.stagesOf(this.returnStages[index]));
  }

  setMasterLevel(level: { volume: number; pan: number }): void {
    this.masterFader = { volume: clampVolume(level.volume), pan: clampPan(level.pan) };
    if (this.masterFaderNodes) {
      this.masterFaderNodes.level.gain.value = faderToGain(this.masterFader.volume);
      this.masterFaderNodes.pan.setPan(this.masterFader.pan);
    }
  }
  private masterMeter: AnalyserNode | null = null;
  private meterScratch = new Float32Array(METER_WINDOW);
  private lanes = new Map<string, LaneNodes>();
  private levels = new Map<string, LaneLevel>();
  private buffers = new Map<string, AudioBuffer>();
  /** Rendered copies of objects (lib/stretch): a warped clip at another
   * tempo, through its markers, or transposed — by `renderKey`. Rendered on
   * the stretch Worker ahead of scheduling (`prepare`); least recently used
   * dropped past MAX_STRETCHED_SECONDS, and with their object. */
  private stretched = new Map<string, { url: string; buffer: AudioBuffer }>();
  private rendering = new Map<string, Promise<void>>();
  private renderer: StretchRenderer | null = null;
  private peakCache = new Map<string, Float32Array>();
  /** Per-object min/max summaries — what `peaks` reads (lib/waveform.ts). */
  private summaries = new Map<string, WaveSummary>();
  private peakQueue: string[] = [];
  private peakLaterQueue: string[] = [];
  private peakLoading = new Set<string>();
  private peakFailed = new Set<string>();
  /** The small MP3 a view named for drawing an object (`ensurePeaks`). */
  private peakPreviews = new Map<string, string>();
  /** Objects whose summary came from that preview — replaced by the exact
   * one when the object itself decodes. */
  private fromPreview = new Set<string>();
  /** Callers of `peaksIdle`, released when the waveform queue drains. */
  private peakIdleWaiters: Array<() => void> = [];
  /** The background fetch `prefetch` runs; aborted by the next, or dispose. */
  private prefetching: AbortController | null = null;
  private disposed = false;
  /** Bumped per replaceSources call so a superseded reload never reschedules. */
  private reloadGeneration = 0;
  private voices: Voice[] = [];
  /** Cap on synth notes one schedule builds — at a dozen nodes a note this
   * is the main thread's budget; the arrangement past it plays its audio
   * and the loop wrap or the next play picks the notes up again. */
  private static readonly MAX_ARRANGEMENT_VOICES = 4000;
  /** The loop's next wrap, already on the clock: the pass after the
   * current one is scheduled with it, so the wrap never waits for a frame
   * (`tick` only moves the bookkeeping over and lays the pass after). */
  private nextWrap: { at: number; from: number; to: number } | null = null;
  /** The current schedule's way into each lane: one gain per lane per
   * schedule, so a reschedule can fade the whole old schedule out under
   * the new one (`reschedule`) instead of cutting it. */
  private genBuses = new Map<string, GainNode>();
  /** Faded-out schedules' buses, disconnected once a later one retires. */
  private retiredBuses: GainNode[][] = [];
  private sources: ClipSource[] = [];
  private midi: MidiSource[] = [];
  private audition: AudioBufferSourceNode | null = null;
  private auditionVoices: OscillatorNode[] = [];
  private auditionId: string | null = null;
  /** Bumped per audition asked for, so the LAST click wins: an audio
   * audition still fetching when a later one lands must not start on top
   * of it (or kill it) once its bytes arrive. */
  private auditionGeneration = 0;
  /** What is auditioning was built from, and how — so an edit to the clip
   * can restart it as edited (`refreshAudition`). */
  private auditionSource: ClipSource | MidiSource | null = null;
  private auditionOptions: AuditionOptions = {};
  /** The audition's own gain into its lane — so a swap for an edited
   * source can fade the old audition out under the new (`refreshAudition`). */
  private auditionBus: GainNode | null = null;
  /** Context time the audition's clip started (room position 0 of the
   * clip): a swap picks up at the phase it has reached since. */
  private auditionStartedAt = 0;
  /** Set by `refreshAudition` for the audition it starts next: swap at this
   * context time, crossfaded, from the phase the old one is at. */
  private auditionSwap: { at: number; phaseSec: number } | null = null;
  /** A clip preview is SOLO: while it plays, every lane's gate closes, so
   * the transport and the launched pads duck under it and come back when
   * it stops (they keep running — nothing is stopped or re-timed). Hearing
   * the clip alone is the point of a preview; layered over the mix, or
   * over its own launched pad, it was inaudible as anything. Notes played
   * live (`auditionNote`) do not solo. */
  private previewSolo = false;
  private setPreviewSolo(on: boolean): void {
    if (this.previewSolo === on) return;
    this.previewSolo = on;
    this.applyGains();
  }
  /** Notes played live (a piano key, a drawn note): polyphonic, beside
   * whatever clip is auditioning, never in its way. */
  private noteVoices: OscillatorNode[] = [];
  private static readonly MAX_NOTE_VOICES = 32;

  /** Timeline seconds at the moment playback started. */
  private startOffset = 0;
  /** ctx.currentTime when playback started. */
  private startedAt = 0;
  private playing = false;

  private muted = new Set<string>();
  private soloed = new Set<string>();
  /** Lanes the Session View has taken over: a launch or a clip stop there
   * silences the lane's timeline clips until "Back to Arrangement" — per
   * lane or for all — hands it back. The transport stopping does not.
   * Playback-only. */
  private overridden = new Set<string>();

  /**
   * The Session View's own clock: the context time of a bar line of the grid
   * launched clips are in time with, while they play WITHOUT the transport.
   *
   * Live has one clock because its transport is always there to be the grid.
   * Here the transport can be stopped and the Session View still playing, so
   * the first launch starts a grid of its own — audible as the click when the
   * click is on, and there to be launched against either way — and the last
   * clip to stop ends it. Null ⇒ the transport's clock is the grid, or
   * nothing is playing at all.
   */
  private sessionClockAt: number | null = null;
  /** Clicks scheduled on that grid, with the times they were scheduled for:
   * ending the grid stops the ones that have not sounded yet. */
  private sessionClicks: Array<{ node: OscillatorNode; at: number }> = [];

  loop = false;
  /**
   * A loop REGION in timeline seconds. Takes precedence over `loop`: the
   * transport plays the region and wraps at its end. Set by the page from a
   * selection or a ruler drag; playback-only, nothing on the wire.
   */
  loopRange: { startSec: number; endSec: number } | null = null;
  /** Click track. Needs `tempo` to know where the beats are. Toggling it
   * while the Session View plays on its own takes effect on those loops:
   * the click belongs to whichever clock is running. */
  get metronome(): boolean {
    return this.clicking;
  }
  set metronome(on: boolean) {
    if (on === this.clicking) return;
    this.clicking = on;
    if (this.sessionClockAt !== null) this.scheduleSessionClicks();
  }
  private clicking = false;
  /** Beats per minute: what the click counts and what the grid's bar lines
   * are measured in. A change while the Session View plays on its own moves
   * those lines, so the click is rescheduled onto them. */
  get tempo(): number {
    return this.bpm;
  }
  set tempo(bpm: number) {
    if (bpm === this.bpm) return;
    this.bpm = bpm;
    if (this.sessionClockAt !== null) this.scheduleSessionClicks();
    // A synced delay or LFO is measured in beats: re-place it.
    this.restageChains();
  }
  private bpm = 120;
  /** The meter the click counts and "launch on the bar" waits for. */
  get meter(): Meter {
    return this.signature;
  }
  set meter(meter: Meter) {
    this.signature = meter;
    if (this.sessionClockAt !== null) this.scheduleSessionClicks();
  }
  private signature: Meter = FOUR_FOUR;
  onEnded: (() => void) | null = null;
  /** Fires with the auditioning source's id, or null when it stops. */
  onAuditionChange: ((id: string | null) => void) | null = null;
  /** Fires whenever a buffer finishes decoding — waveforms can draw now. */
  onDecoded: (() => void) | null = null;

  /**
   * Timeline extent. Sources with no declared duration only contribute once
   * their buffer has decoded, so this grows from `fallbackDuration` to the real
   * length as loading completes.
   */
  get duration(): number {
    const measured = this.sources.reduce((m, s) => Math.max(m, s.startSec + this.lengthOf(s)), 0);
    const midiEnd = this.midi.reduce((m, s) => Math.max(m, s.startSec + s.durationSec), 0);
    return Math.max(measured, midiEnd, this.fallbackDuration);
  }

  /** Shown before anything has decoded, so the transport isn't stuck at 0:00. */
  fallbackDuration = 0;

  private lengthOf(src: ClipSource): number {
    if (src.durationSec !== undefined) return src.durationSec;
    const buffer = this.buffers.get(src.url);
    return buffer ? Math.max(0, buffer.duration - (src.offsetSec ?? 0)) / (src.tempoRatio ?? 1) : 0;
  }

  /**
   * What a source needs rendered to play, or null when the object plays as
   * it is (under a rate, perhaps). Re-Pitch without markers is a rate; with
   * markers it is a render (the rate changes per segment). A transpose on a
   * warped clip is a render; on an unwarped one (`pitchByRate`) a rate.
   */
  private static renderSpec(src: ClipSource): RenderSpec | null {
    const repitch = src.repitch === true;
    if (repitch && !src.warpMap) return null;
    const spec: RenderSpec = {
      map: src.warpMap ? { kind: "markers", ...src.warpMap } : { kind: "ratio", ratio: src.tempoRatio ?? 1 },
      mode: repitch ? WARP_MODE_REPITCH : warpModeOf(src.warpMode),
      semitones: repitch || src.pitchByRate ? 0 : (src.semitones ?? 0),
      ...(src.warpSettings && !repitch ? { settings: src.warpSettings, ...(src.gridBpm !== undefined ? { gridBpm: src.gridBpm } : {}) } : {}),
    };
    return isIdentity(spec) ? null : spec;
  }

  private static renderKey(url: string, spec: RenderSpec): string {
    const map =
      spec.map.kind === "ratio"
        ? `r${spec.map.ratio.toFixed(5)}`
        : `m${spec.map.bpm.toFixed(3)}/${spec.map.fallbackBpm.toFixed(3)}/${spec.map.markers.map((m) => `${m.beat.toFixed(4)}@${m.sec.toFixed(4)}`).join(",")}`;
    return `${url}|${map}|w${spec.mode}${warpSettingsKey(spec.settings)}${spec.gridBpm !== undefined ? `@${spec.gridBpm.toFixed(3)}` : ""}|${spec.semitones.toFixed(3)}`;
  }

  /** Where stretch jobs run — the Worker by default; a test swaps in its own. */
  useStretchRenderer(renderer: StretchRenderer): void {
    this.renderer = renderer;
  }

  private stretchRenderer(): StretchRenderer {
    if (!this.renderer) {
      this.renderer = defaultRenderer();
      // Which engine the Worker settled on — one line, so a slow render has
      // a first thing to check.
      this.renderer.engine().then(
        (name) => console.info(`[player] stretch engine: ${name}`),
        () => undefined,
      );
    }
    return this.renderer;
  }

  /** Rendered copies are dropped (oldest first, never one a current source
   * plays) once they hold more than this many seconds. */
  private static readonly MAX_STRETCHED_SECONDS = 600;

  /**
   * Render, off the main thread, every copy these sources play that isn't
   * ready yet — what `load`, `replaceSources`, an audition and a launch
   * await before scheduling, so an edit's old audio plays on until its new
   * render is in. Objects not decoded yet are skipped (nothing to render).
   * A render a newer edit superseded, or one that failed, leaves its key
   * missing; `resolve` then renders inline as a last resort.
   */
  async prepare(sources: readonly ClipSource[], exact = false): Promise<void> {
    // A render a NEWER edit superseded on its worker lane rejects late — a
    // prepare that joined it then had nothing, and `playingBuffer` rendered
    // on the main thread (jank, a glitch). Asked for once more, it is a
    // fresh job that the edit after it no longer stands in front of.
    await this.renderMissing(sources, exact);
    await this.renderMissing(sources, exact);
    this.pruneStretched();
  }

  /** One round of rendering: every copy these sources play that isn't
   * ready, joined where a job already runs. A source that can play by
   * APPROXIMATION meanwhile (`approximation`) is not waited for unless
   * `exact`: its job runs on, and `renderArrived` swaps the exact copy in
   * under whatever is sounding when it lands. */
  private async renderMissing(sources: readonly ClipSource[], exact: boolean): Promise<void> {
    const jobs: Promise<void>[] = [];
    const seen = new Set<string>();
    for (const src of sources) {
      const spec = ArrangementPlayer.renderSpec(src);
      const original = this.buffers.get(src.url);
      if (!spec || !original) continue;
      const key = ArrangementPlayer.renderKey(src.url, spec);
      if (seen.has(key) || this.stretched.has(key)) continue;
      seen.add(key);
      let job = this.rendering.get(key);
      if (!job) {
        const lane = src.id.split("#")[0]!;
        job = this.stretchRenderer()
          .render(src.url, original, spec, lane)
          .then((pcm) => {
            this.stretched.set(key, { url: src.url, buffer: this.toBuffer(pcm, original.sampleRate) });
            this.renderArrived(key);
          })
          .catch((err: unknown) => {
            if (!(err instanceof Superseded)) console.warn("[player] stretch failed", src.url, err);
          })
          .finally(() => this.rendering.delete(key));
        this.rendering.set(key, job);
      }
      if (exact || !this.approximation(src)) jobs.push(job);
    }
    await Promise.all(jobs);
  }

  /**
   * Live's instant pitch: a transpose on a warped clip is heard the moment
   * the knob moves, not when the vocoder is done. Until the exact render
   * lands, the clip plays its PITCH-FLAT copy (the object itself at the
   * Set's tempo, or the stretch it was already playing) under a playback
   * rate of 2^(semitones/12) — the right pitch, a little fast or slow for
   * the moment the render takes — and `renderArrived` then crossfades the
   * exact copy in at the phase reached. Null when there is nothing ready
   * to approximate with (the copy must already be here: an approximation
   * is never worth a render of its own), or nothing to approximate.
   */
  private approximation(src: ClipSource): { buffer: AudioBuffer; rate: number } | null {
    const spec = ArrangementPlayer.renderSpec(src);
    if (!spec || spec.mode === WARP_MODE_REPITCH || Math.abs(spec.semitones) < 1e-6) return null;
    const flat: RenderSpec = { ...spec, semitones: 0 };
    const buffer = isIdentity(flat) ? this.buffers.get(src.url) : this.stretched.get(ArrangementPlayer.renderKey(src.url, flat))?.buffer;
    return buffer ? { buffer, rate: 2 ** (spec.semitones / 12) } : null;
  }

  /** The render key a source plays by, when it plays a render. */
  private static keyOf(src: ClipSource): string | null {
    const spec = ArrangementPlayer.renderSpec(src);
    return spec ? ArrangementPlayer.renderKey(src.url, spec) : null;
  }

  /** Render keys that landed since the last `followRenders`, which runs
   * once per turn for all of them. */
  private arrived = new Set<string>();

  /** A render is in: every voice sounding its source by approximation is
   * swapped for the exact copy — the transport at the playhead, a launched
   * pad and the preview at their phase — crossfaded, never restarted. */
  private renderArrived(key: string): void {
    if (this.disposed) return;
    if (this.arrived.size === 0) queueMicrotask(() => void this.followRenders());
    this.arrived.add(key);
  }

  private async followRenders(): Promise<void> {
    const keys = new Set(this.arrived);
    this.arrived.clear();
    if (keys.size === 0 || this.disposed) return;
    const landed = (src: ClipSource | MidiSource): boolean => {
      if ("notes" in src) return false;
      const key = ArrangementPlayer.keyOf(src);
      return key !== null && keys.has(key);
    };
    if (this.playing && this.sources.some(landed)) await this.reschedule();
    const slots: Array<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop: boolean }> = [];
    for (const [laneId, v] of this.slotVoices) if (landed(v.source)) slots.push({ laneId, clipId: v.clipId, source: v.source, loop: v.loop });
    if (slots.length > 0) this.swapSlots(slots);
    if (this.auditionSource && landed(this.auditionSource)) await this.swapAudition(this.auditionSource);
  }

  private toBuffer(pcm: { numberOfChannels: number; length: number; getChannelData(c: number): Float32Array }, sampleRate: number): AudioBuffer {
    const out = this.ensureContext().createBuffer(pcm.numberOfChannels, Math.max(1, pcm.length), sampleRate);
    for (let c = 0; c < pcm.numberOfChannels; c++) out.copyToChannel(pcm.getChannelData(c) as Float32Array<ArrayBuffer>, c);
    return out;
  }

  private pruneStretched(): void {
    const keep = new Set<string>();
    for (const src of this.sources) {
      const spec = ArrangementPlayer.renderSpec(src);
      if (spec) keep.add(ArrangementPlayer.renderKey(src.url, spec));
    }
    let total = 0;
    for (const v of this.stretched.values()) total += v.buffer.duration;
    for (const [key, v] of this.stretched) {
      if (total <= ArrangementPlayer.MAX_STRETCHED_SECONDS) break;
      if (keep.has(key)) continue;
      this.stretched.delete(key);
      total -= v.buffer.duration;
    }
  }

  /**
   * The buffer a source plays: the object, or its rendered copy — ready
   * from `prepare`, or (a caller that didn't prepare) rendered here on the
   * main thread, once, and cached like any other.
   */
  private playingBuffer(src: ClipSource): { buffer: AudioBuffer; approxRate: number } | undefined {
    const original = this.buffers.get(src.url);
    const spec = ArrangementPlayer.renderSpec(src);
    if (!original) return undefined;
    if (!spec) return { buffer: original, approxRate: 1 };
    const key = ArrangementPlayer.renderKey(src.url, spec);
    const cached = this.stretched.get(key);
    if (cached) {
      // Most recently used goes last, so pruning takes the stalest first.
      this.stretched.delete(key);
      this.stretched.set(key, cached);
      return { buffer: cached.buffer, approxRate: 1 };
    }
    // The exact copy is still on the Worker: the pitch-flat copy under a
    // rate stands in until it lands (`approximation`).
    const approx = this.approximation(src);
    if (approx) return { buffer: approx.buffer, approxRate: approx.rate };
    const out = this.toBuffer(renderWarped(original, spec), original.sampleRate);
    this.stretched.set(key, { url: src.url, buffer: out });
    return { buffer: out, approxRate: 1 };
  }

  /**
   * Where and how a source plays, `lateSec` room seconds after its start
   * (a clip straddling the playhead). Object seconds are scaled onto a
   * rendered copy, or left in buffer time under a playback rate (Re-Pitch
   * without markers, or an unwarped clip's transpose); the declared length
   * (room seconds) becomes buffer seconds for `start()`. Null when the
   * object has not decoded or nothing is left to play.
   */
  private resolve(src: ClipSource, lateSec = 0): Resolved | null {
    const playing = this.playingBuffer(src);
    if (!playing) return null;
    const { buffer, approxRate } = playing;
    const rendered = ArrangementPlayer.renderSpec(src) !== null;
    // A marker-mapped source's offsets are already room seconds on its grid.
    const ratio = src.warpMap ? 1 : (src.tempoRatio ?? 1);
    const scale = rendered ? 1 / ratio : 1; // object seconds → buffer seconds
    const pitchRate = src.pitchByRate && src.semitones ? 2 ** (src.semitones / 12) : 1;
    // A render plays at 1 — or, standing in for one, the pitch-flat copy
    // at the transpose's rate (`approximation`).
    const rate = rendered ? approxRate : (src.repitch ? ratio : 1) * pitchRate;
    const declared = Math.max(0, this.lengthOf(src) - lateSec); // room seconds left
    const offset = Math.max(0, (src.offsetSec ?? 0) * scale + lateSec * rate);
    if (offset >= buffer.duration) return null;
    const playFor = Math.min(declared * rate, buffer.duration - offset);
    if (playFor <= 0) return null;
    return { buffer, offset, playFor, rate, outSec: playFor / rate, scale };
  }

  /** The transients of the object behind `url` (the clip editor's grey
   * ticks), computed once per object off the main thread. */
  async onsetsFor(url: string): Promise<Onset[]> {
    const buffer = await this.bufferFor(url);
    return this.stretchRenderer().onsets(url, buffer);
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get position(): number {
    if (!this.playing || !this.ctx) return this.startOffset;
    // A quantized jump waiting for its line: the old run is still what sounds.
    const j = this.jumpFrom;
    if (j && this.ctx.currentTime < j.until) return j.offset + Math.max(0, this.ctx.currentTime - j.startedAt);
    // A start held back for the next bar line: the playhead waits with it
    // rather than running negative before the music comes in.
    return this.startOffset + Math.max(0, this.ctx.currentTime - this.startedAt);
  }

  /** Which sources can actually sound — decoded audio, plus MIDI clips with
   * notes (those need no bytes at all). The rest are silent gaps. */
  get playableCount(): number {
    return (
      this.sources.filter((s) => this.buffers.has(s.url)).length +
      this.midi.filter((s) => s.notes.length > 0).length
    );
  }

  /** MIDI clips are schedulable the moment they are handed over — no fetch. */
  setMidiSources(midi: MidiSource[]): void {
    this.midi = midi;
  }

  /**
   * Fetch and decode everything these sources reference.
   *
   * Created here rather than in the constructor because browsers only allow an
   * AudioContext to start from a user gesture, and loading is always triggered
   * by the play button.
   */
  async load(
    sources: ClipSource[],
    onProgress: (p: LoadProgress) => void,
    signal: AbortSignal,
  ): Promise<LoadProgress> {
    this.sources = sources;
    this.ensureContext();

    const urls = ArrangementPlayer.missingInPlayOrder(sources, this.buffers);
    const progress: LoadProgress = { loaded: 0, total: urls.length, bytes: 0, failed: 0 };
    onProgress({ ...progress });

    await this.fetchEach(urls, signal, (url, ok) => {
      // One unreadable clip must not sink the whole arrangement — it plays
      // as a gap, and the caller reports how many are missing.
      if (ok) progress.bytes += this.objectBytes.get(url) ?? 0;
      else progress.failed++;
      progress.loaded++;
      onProgress({ ...progress });
    });
    if (!signal.aborted) await this.prepare(sources);
    return progress;
  }

  /**
   * What Play waits for: the objects that sound in the next `PLAY_READY_SEC`.
   * The rest keep downloading behind it, earliest first, each joining the
   * playback as it lands (`layLanded`). Waiting for all of them made one long
   * bounce late in a jam (53 MB of 88, j_mvyktzqw) the price of the first bar.
   *
   * `onProgress` keeps hearing the objects behind the Play after this resolves.
   * With nothing near `fromSec` and nothing else that can sound, it waits for
   * everything, so an arrangement that cannot play is still reported as such.
   */
  async loadForPlay(
    sources: ClipSource[],
    fromSec: number,
    onProgress: (p: LoadProgress) => void,
    signal: AbortSignal,
  ): Promise<LoadProgress> {
    this.sources = sources;
    this.ensureContext();
    this.prefetching?.abort();
    const urls = ArrangementPlayer.missingInPlayOrder(sources, this.buffers);
    const soon = new Set(this.soundingSoon(sources, fromSec));
    const progress: LoadProgress = { loaded: 0, total: urls.length, bytes: 0, failed: 0 };
    onProgress({ ...progress });
    const landed = (url: string, ok: boolean): void => {
      if (ok) progress.bytes += this.objectBytes.get(url) ?? 0;
      else progress.failed++;
      progress.loaded++;
      onProgress({ ...progress });
    };
    const later = urls.filter((url) => !soon.has(url));

    await this.fetchEach(urls.filter((url) => soon.has(url)), signal, landed);
    if (signal.aborted) return { ...progress };
    if (this.playableCount === 0) {
      await this.fetchEach(later, signal, landed);
      if (!signal.aborted) await this.prepare(sources);
      return { ...progress };
    }
    await this.prepare(sources);
    if (!signal.aborted) {
      const controller = new AbortController();
      this.prefetching = controller;
      this.background = this.fetchEach(later, controller.signal, landed);
    }
    return { ...progress };
  }

  /** Whether Play from `fromSec` has anything to wait for. */
  needsLoadForPlay(sources: readonly ClipSource[], fromSec: number): boolean {
    return this.soundingSoon(sources, fromSec).length > 0;
  }

  /** The undecoded objects these sources sound in the next `PLAY_READY_SEC`
   * from `fromSec`, past the loop's wrap when one comes first. */
  private soundingSoon(sources: readonly ClipSource[], fromSec: number): string[] {
    const span = this.loopSpan();
    const end = fromSec + PLAY_READY_SEC;
    const windows: Array<[number, number]> =
      span && fromSec < span.to && end > span.to
        ? [[fromSec, span.to], [span.from, Math.min(span.to, span.from + end - span.to)]]
        : [[fromSec, end]];
    const soon = new Set<string>();
    for (const src of sources) {
      if (this.buffers.has(src.url)) continue;
      // A source with no declared length is as long as its unread object:
      // only its start can rule it out.
      const srcEnd = src.durationSec === undefined ? Infinity : src.startSec + src.durationSec;
      if (windows.some(([a, b]) => src.startSec < b && srcEnd > a)) soon.add(src.url);
    }
    return [...soon];
  }

  /**
   * An object landed under a running transport: its clips join what is sounding
   * from the playhead on, and nothing already sounding is touched. A clip that
   * needs a render reschedules instead, which waits for that render. Called in
   * the same step that stores the buffer, so a schedule either has this object
   * already or will hear of it here, never both.
   */
  private layLanded(url: string): void {
    if (!this.playing || !this.ctx) return;
    const clips = this.sources.filter((s) => s.url === url);
    if (clips.length === 0) return;
    if (clips.some((s) => ArrangementPlayer.renderSpec(s) !== null)) {
      void this.reschedule();
      return;
    }
    const now = this.ctx.currentTime + TRANSPORT_LEAD_SEC;
    const wrap = this.nextWrap;
    const lay = (from: number, to: number, anchor: number): void => {
      const passEnd = to === Infinity ? Infinity : anchor + (to - from);
      for (const src of clips) this.layAudio(src, from, to, anchor, passEnd);
    };
    if (wrap && now >= wrap.at) {
      // Already in the next pass; `tick` lays the one after it, this object included.
      lay(wrap.from + (now - wrap.at), wrap.to, now);
    } else {
      const anchor = Math.max(now, this.startedAt);
      lay(this.startOffset + (anchor - this.startedAt), wrap ? wrap.to : Infinity, anchor);
      if (wrap) lay(wrap.from, wrap.to, wrap.at);
    }
    this.applyGains();
  }

  /**
   * Resolves once nothing is downloading the arrangement's objects, and still
   * nothing a moment later. For a small read of one of them: made during its
   * download, the browser's cache keeps neither (three stems fetched again on
   * every visit), and made after, the cache answers it without a request.
   */
  async fetchesSettled(): Promise<void> {
    do {
      await Promise.allSettled([...this.decoding.values(), this.background]);
      await new Promise((resolve) => setTimeout(resolve, PEAKS_SETTLE_MS));
    } while (!this.disposed && (this.decoding.size > 0 || this.backgroundRunning));
  }

  /** The objects these sources need that aren't decoded yet, the earliest on
   * the timeline first — so what plays first lands first. */
  private static missingInPlayOrder(sources: readonly ClipSource[], have: ReadonlyMap<string, AudioBuffer>): string[] {
    const first = new Map<string, number>();
    for (const s of sources) {
      if (have.has(s.url)) continue;
      const at = first.get(s.url);
      if (at === undefined || s.startSec < at) first.set(s.url, s.startSec);
    }
    return [...first.keys()].sort((a, b) => first.get(a)! - first.get(b)!);
  }

  /**
   * Fetch and decode `urls` through `bufferFor`, a few at a time — so an
   * object a waveform is already fetching is joined, never fetched twice, and
   * one object decodes while the next downloads. Not all at once: these are
   * multi-megabyte 24-bit renders, and a browser that opens eight of them at
   * once just makes every one slower. `each` hears every object that settles
   * before the signal aborts; a failure is logged, not thrown.
   */
  private async fetchEach(
    urls: readonly string[],
    signal: AbortSignal | undefined,
    each: (url: string, ok: boolean) => void,
  ): Promise<void> {
    let next = 0;
    const worker = async (): Promise<void> => {
      while (next < urls.length && !signal?.aborted) {
        const url = urls[next++]!;
        let ok = true;
        try {
          await this.bufferFor(url, signal);
        } catch (err) {
          if (signal?.aborted) return;
          console.warn("[player] could not load", url, err);
          ok = false;
        }
        each(url, ok);
      }
    };
    await Promise.all(Array.from({ length: Math.min(LOAD_CONCURRENCY, urls.length) }, worker));
  }

  /**
   * Audition one clip on demand — what clicking a clip does. ONE contract
   * for audio and MIDI (`auditionMidiOnce` is the notes half of it):
   *
   * - Independent of the transport, so it works while paused; the
   *   transport's Stop ends it, a pad launch does not.
   * - Through the clip's lane — its device chain, fader and pan — past the
   *   lane's mute/solo gate (`auditionOut`): a muted lane's clip is still
   *   audible when clicked (silence would just look broken), and it sounds
   *   the way it sounds in the mix.
   * - `sync`: starts on the next bar line of whichever clock runs (Live's
   *   non-Raw preview); `loop`: keeps going until the next preview or a stop.
   * - One at a time, and the LAST click wins: a later audition supersedes
   *   one still fetching (`auditionGeneration`).
   *
   * Reuses whatever is already decoded, so auditioning during or after playback
   * costs nothing; a clip still loading for a waveform, a pad or the
   * transport is joined, not fetched again (`bufferFor`).
   */
  async auditionOnce(src: ClipSource, signal?: AbortSignal, options: AuditionOptions = {}): Promise<void> {
    const ctx = this.ensureContext();
    const generation = ++this.auditionGeneration;
    if (ctx.state === "suspended") await ctx.resume();

    await this.bufferFor(src.url, signal);
    await this.prepare([src]);
    if (generation !== this.auditionGeneration) return; // a later click took over
    const swap = this.takeAuditionSwap();
    this.endAudition(swap?.at);
    const resolved = this.resolve(src);
    if (!resolved) return;
    const { buffer, offset, playFor: length, rate } = resolved;
    const periodSec = length / rate; // one pass of the clip, room seconds
    // A swap picks the clip up where the old audition is in it.
    const phase = swap ? swap.phaseSec % Math.max(periodSec, 1e-6) : 0;
    const from = offset + phase * rate;
    if (from >= offset + length) return;

    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.playbackRate.value = rate;
    // Live's browser preview: a loop keeps going until the next preview or a
    // stop, so you can hear it against the Set. The loop window is the clip's
    // own region in the (possibly stretched) object.
    if (options.loop) {
      node.loop = true;
      node.loopStart = offset;
      node.loopEnd = offset + length;
    }
    const when = swap?.at ?? this.auditionStart(options);
    node.connect(this.auditionBusFor(src.laneId, swap ? when : undefined, options.level ? levelGain(buffer, offset, length) : 1));
    node.onended = () => {
      if (this.audition !== node) return;
      this.audition = null;
      this.auditionId = null;
      this.setPreviewSolo(false);
      this.onAuditionChange?.(null);
    };
    this.setPreviewSolo(true);
    if (options.loop) node.start(when, from);
    else node.start(when, from, length - phase * rate);
    this.audition = node;
    this.auditionId = src.id;
    this.auditionSource = src;
    this.auditionOptions = options;
    this.auditionStartedAt = when - phase;
    this.onAuditionChange?.(src.id);
  }

  /** The gain an audition plays its lane through. Starting a swap, it fades
   * in at `fadeAt` over the old one fading out. */
  private auditionBusFor(laneId: string, fadeAt?: number, gain = 1): GainNode {
    const ctx = this.ensureContext();
    const bus = ctx.createGain();
    bus.gain.value = gain;
    bus.connect(this.auditionOut(laneId));
    if (fadeAt !== undefined) {
      bus.gain.setValueAtTime(0, fadeAt);
      bus.gain.linearRampToValueAtTime(gain, fadeAt + RESCHEDULE_XFADE_SEC);
    }
    this.auditionBus = bus;
    return bus;
  }

  /** The swap `refreshAudition` asked for, once. */
  private takeAuditionSwap(): { at: number; phaseSec: number } | null {
    const swap = this.auditionSwap;
    this.auditionSwap = null;
    return swap;
  }

  /**
   * End the audition that is playing: now, or — a swap — faded out at
   * `fadeAt` with its voices stopped just after, the preview's solo kept
   * for the one taking its place. Notes played live are left alone.
   */
  private endAudition(fadeAt?: number): void {
    const bus = this.auditionBus;
    this.auditionBus = null;
    const voices: Array<AudioScheduledSourceNode> = [...this.auditionVoices];
    if (this.audition) voices.push(this.audition);
    this.auditionVoices = [];
    this.audition = null;
    if (fadeAt === undefined) {
      this.setPreviewSolo(false);
      for (const node of voices) {
        try {
          node.onended = null;
          stopVoice(node);
          node.disconnect();
        } catch {
          // already finished
        }
      }
      bus?.disconnect();
    } else {
      if (bus) {
        bus.gain.setValueAtTime(1, fadeAt);
        bus.gain.linearRampToValueAtTime(0, fadeAt + RESCHEDULE_XFADE_SEC);
      }
      for (const node of voices) {
        try {
          node.onended = null;
          stopVoice(node, fadeAt + RESCHEDULE_XFADE_SEC + 0.005);
        } catch {
          // already finished
        }
      }
    }
    if (this.auditionId !== null && fadeAt === undefined) {
      this.auditionId = null;
      this.onAuditionChange?.(null);
    }
  }

  /** When an audition comes in: the next bar line of whichever clock runs
   * when asked to sync and one does, else a hair ahead so note 1 gets its
   * attack. */
  private auditionStart(options: AuditionOptions): number {
    const ctx = this.ensureContext();
    if (options.sync && (this.playing || this.sessionClockAt !== null) && this.tempo > 0) {
      return Math.max(ctx.currentTime + AUDITION_LEAD_SEC, this.nextGridLine(beatsPerBar(this.meter)));
    }
    return ctx.currentTime + AUDITION_LEAD_SEC;
  }

  /**
   * Audition one MIDI clip — the notes half of `auditionOnce`'s contract:
   * same lane routing, same `sync` and `loop`, same last-click-wins. A loop
   * repeats the clip's passes (chance rolled afresh per pass, as a launch
   * does) up to the launch cap; a one-shot plays the notes once.
   */
  async auditionMidiOnce(src: MidiSource, options: AuditionOptions = {}): Promise<void> {
    const ctx = this.ensureContext();
    const generation = ++this.auditionGeneration;
    if (ctx.state === "suspended") await ctx.resume();
    if (generation !== this.auditionGeneration) return;
    if (src.notes.length === 0 && !src.reroll) return;

    const swap = this.takeAuditionSwap();
    this.endAudition(swap?.at);
    const period = Math.max(src.durationSec, 0.01);
    // A swap picks the clip up where the old audition is in it: the first
    // pass is laid from that phase, notes before it skipped.
    const phase = swap ? swap.phaseSec % period : 0;
    const when = swap?.at ?? this.auditionStart(options);
    const base = when - phase;
    const out = this.auditionBusFor(src.laneId, swap ? when : undefined);
    const patch = this.patchFor(src.laneId);
    const voices = this.auditionVoices;
    const finish = (): void => {
      if (this.auditionVoices !== voices) return;
      this.auditionVoices = [];
      this.auditionId = null;
      this.setPreviewSolo(false);
      this.onAuditionChange?.(null);
    };
    this.setPreviewSolo(true);
    this.auditionId = src.id;
    this.auditionSource = src;
    this.auditionOptions = options;
    this.auditionStartedAt = base;
    this.onAuditionChange?.(src.id);
    this.feedMidi({
      src,
      out,
      patch,
      base,
      period,
      phase,
      passes: options.loop ? ArrangementPlayer.MIDI_LAUNCH_LOOPS : 1,
      next: 0,
      nodes: voices,
      last: null,
      lastEnd: -Infinity,
      live: () => this.auditionVoices === voices,
      // `onended`, not a listener: ending the audition early nulls it.
      done: (last) => {
        if (last) last.onended = finish;
        else finish();
      },
    });
  }

  /**
   * An edit reaches the preview: if `src` is the clip auditioning and it no
   * longer sounds like what started (`playbackSignature`), the audition is
   * SWAPPED for the edited one — at the phase it has reached, a lead from
   * now, the old fading out under the new (`RESCHEDULE_XFADE_SEC`) — so a
   * transpose or a warp change is heard as a change in the clip that is
   * playing, never as the clip starting over. Anything else is left alone.
   * A render still on its way keeps the old audition sounding meanwhile.
   */
  async refreshAudition(src: ClipSource | MidiSource): Promise<void> {
    const was = this.auditionSource;
    if (!was || this.auditionId !== src.id) return;
    if (playbackSignature(was) === playbackSignature(src)) return;
    await this.swapAudition(src);
  }

  /** The swap itself: `src` in place of the audition playing, at its phase. */
  private async swapAudition(src: ClipSource | MidiSource): Promise<void> {
    const ctx = this.ensureContext();
    if (!("notes" in src)) {
      // The render first: the swap time is picked once it is in, so the old
      // audition is never faded out ahead of a new one that isn't ready.
      await this.bufferFor(src.url);
      await this.prepare([src]);
      if (this.auditionId !== src.id) return; // stopped or replaced meanwhile
    }
    const at = ctx.currentTime + TRANSPORT_LEAD_SEC;
    const phaseSec = Math.max(0, at - this.auditionStartedAt);
    this.auditionSwap = { at, phaseSec };
    const options = { ...this.auditionOptions };
    if ("notes" in src) await this.auditionMidiOnce(src, options);
    else await this.auditionOnce(src, undefined, options);
  }

  /**
   * One note, now, through its lane — a piano key pressed, a note drawn.
   * Polyphonic: a chord is every key you hold, and a key never cuts a clip
   * auditioning beside it. Returns the key's release: call it to end the
   * note early (key up); unreleased, it ends at `note.durationSec`. Capped,
   * oldest first, so a held cluster cannot pile up voices.
   */
  auditionNote(laneId: string, note: MidiNoteEvent): () => void {
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") void ctx.resume();
    while (this.noteVoices.length >= ArrangementPlayer.MAX_NOTE_VOICES) this.killVoices([this.noteVoices.shift()!]);
    const node = this.startTone({ ...note, startSec: 0 }, ctx.currentTime + AUDITION_LEAD_SEC, this.auditionOut(laneId), this.patchFor(laneId));
    this.noteVoices.push(node);
    const forget = () => {
      const i = this.noteVoices.indexOf(node);
      if (i >= 0) this.noteVoices.splice(i, 1);
    };
    node.addEventListener("ended", forget);
    return () => {
      if (!this.noteVoices.includes(node)) return;
      // A hair later than now: the voice's own `ended` then tears its layers
      // down (synth-voice.ts), instead of a stop in the past that some
      // browsers never report.
      try {
        node.stop(ctx.currentTime + AUDITION_LEAD_SEC);
      } catch {
        // already finished
      }
      forget();
    };
  }

  /** Where an audition plays: through the lane's device chain, fader and
   * pan — past the mute/solo gate, so a muted lane still auditions — when
   * the lane exists, else the master. A soloed return closes every other
   * lane's direct path; an audition then goes to the master so a click is
   * never silent. */
  private auditionOut(laneId: string): AudioNode {
    if (this.returnLevels.some((r) => r.solo) && !this.soloed.has(laneId)) return this.master!;
    this.laneGain(laneId);
    return this.lanes.get(laneId)?.chain.input ?? this.master!;
  }

  stopAudition(): void {
    this.auditionGeneration++;
    this.auditionSwap = null;
    this.killVoices(this.noteVoices);
    this.noteVoices = [];
    this.endAudition();
  }

  private recorder: MediaRecorder | null = null;
  private recordStream: MediaStream | null = null;
  private recordChunks: Blob[] = [];
  private recordStartSec = 0;

  get isRecording(): boolean {
    return this.recorder !== null;
  }

  /**
   * Record the microphone from the transport's CURRENT position. Starts the
   * transport if it is stopped, so what is recorded lines up with what was
   * heard. Nothing is compensated for input latency — a take lands a few ms
   * late; nudge it. Throws when the browser refuses the mic.
   */
  async startRecording(countInBars = 0): Promise<void> {
    if (this.recorder) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
    // Count-in: a bar (or more) of clicks from a stopped transport before
    // anything rolls, so the first downbeat isn't the one you're reacting to.
    if (countInBars > 0 && this.tempo > 0 && !this.playing) {
      const ctx = this.ensureContext();
      if (ctx.state === "suspended") await ctx.resume();
      const beat = (60 / this.tempo) * beatUnit(this.meter);
      const beats = Math.round(countInBars * this.meter.numerator);
      const from = ctx.currentTime + 0.05;
      for (let i = 0; i < beats; i++) this.click(from + i * beat, i % this.meter.numerator === 0, null);
      await new Promise<void>((resolve) => setTimeout(resolve, (from + beats * beat - ctx.currentTime) * 1000));
      if (this.recorder) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
    }
    if (!this.playing) await this.play();
    const recorder = new MediaRecorder(stream);
    this.recordChunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.recordChunks.push(e.data);
    };
    this.recorder = recorder;
    this.recordStream = stream;
    this.recordStartSec = this.position;
    recorder.start(250);
  }

  /** Stop and hand back the take, decoded, with where on the timeline it began. */
  /**
   * Measure the round trip from the speakers to the mic: record while a
   * click train plays at known times on the audio clock, then find the lag
   * that lines the recording up with it (lib/latency.ts). That lag is
   * exactly what the record offset compensates. Null when nothing came back
   * (no mic, silence, the room too noisy to find the clicks).
   */
  async calibrateLatency(): Promise<LatencyEstimate | null> {
    if (this.recorder) throw new Error("A take is recording — stop it first.");
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
    // Nothing else may sound during the measurement: a metronome tick or a
    // clip transient in the recording is something the matched filter can
    // lock onto instead of the clicks. Playback resumes where it was.
    const wasPlaying = this.playing;
    if (wasPlaying) this.pause();
    if (this.killSlots()) this.onSessionChange?.(this.sessionPlaying);
    this.stopAudition();
    try {
      const ctx = this.ensureContext();
      if (ctx.state === "suspended") await ctx.resume();
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      const stopped = new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
      });
      recorder.start();
      const t0 = ctx.currentTime;
      for (const t of CALIBRATION_CLICKS) this.click(t0 + t, true, null);
      await new Promise<void>((resolve) => setTimeout(resolve, CALIBRATION_SECONDS * 1000));
      recorder.stop();
      await stopped;
      const blob = new Blob(chunks, { type: recorder.mimeType });
      if (blob.size === 0) return null;
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      // Mono fold: the clicks are wherever the mic is.
      const mono = new Float32Array(buffer.length);
      for (let c = 0; c < buffer.numberOfChannels; c++) {
        const data = buffer.getChannelData(c);
        for (let i = 0; i < mono.length; i++) mono[i] = (mono[i] ?? 0) + (data[i] ?? 0) / buffer.numberOfChannels;
      }
      return estimateLatency(mono, buffer.sampleRate, CALIBRATION_CLICKS);
    } finally {
      stream.getTracks().forEach((t) => t.stop());
      if (wasPlaying) void this.play();
    }
  }

  async stopRecording(): Promise<{ buffer: AudioBuffer; startSec: number } | null> {
    const recorder = this.recorder;
    if (!recorder) return null;
    const startSec = this.recordStartSec;
    const done = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await done;
    this.recorder = null;
    this.recordStream?.getTracks().forEach((t) => t.stop());
    this.recordStream = null;
    const blob = new Blob(this.recordChunks, { type: recorder.mimeType });
    this.recordChunks = [];
    if (blob.size === 0) return null;
    const buffer = await this.decode(await blob.arrayBuffer());
    return { buffer, startSec };
  }

  /** Decode arbitrary audio bytes (a dropped file) with the same context that
   * plays them. Any format the browser can decode. */
  async decode(bytes: ArrayBuffer): Promise<AudioBuffer> {
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") await ctx.resume();
    return decodeObject(ctx, bytes);
  }

  /**
   * The object behind a URL played `semitones` higher (or lower) by
   * resampling — pitch and length change together, as a repitched sample
   * does. Rendered offline at the live context's rate; the result is a new
   * object whose length is the old one's ÷ 2^(semitones/12).
   */
  async renderRepitched(url: string, semitones: number, offsetSec = 0, durationSec?: number): Promise<AudioBuffer> {
    const buffer = await this.bufferFor(url);
    const rate = 2 ** (semitones / 12);
    const span = Math.max(0.01, Math.min(durationSec ?? buffer.duration - offsetSec, buffer.duration - offsetSec));
    const outSec = span / rate;
    const offline = new OfflineAudioContext(buffer.numberOfChannels, Math.ceil(outSec * buffer.sampleRate), buffer.sampleRate);
    const node = offline.createBufferSource();
    node.buffer = buffer;
    node.playbackRate.value = rate;
    node.connect(offline.destination);
    node.start(0, offsetSec, span);
    return offline.startRendering();
  }

  /**
   * The decoded object behind a URL, fetching it if the transport hasn't.
   *
   * Single-flight: every caller asking for an object in flight — N clips
   * sharing it (paste, duplicate), a waveform and the Play that follows it —
   * joins the one fetch. So the fetch itself is never aborted: `signal` only
   * stops THIS caller waiting, and the object lands for whoever asks next.
   */
  async bufferFor(url: string, signal?: AbortSignal): Promise<AudioBuffer> {
    const cached = this.buffers.get(url);
    if (cached) return cached;
    let job = this.decoding.get(url);
    if (!job) {
      job = this.fetchObject(url).finally(() => this.decoding.delete(url));
      // A caller that stopped waiting must not leave the failure unheard.
      job.catch(() => undefined);
      this.decoding.set(url, job);
    }
    return signal ? untilAborted(job, signal) : job;
  }
  private decoding = new Map<string, Promise<AudioBuffer>>();
  /** The objects going in behind the view or a Play (`prefetch`, `loadForPlay`). */
  private backgroundRun: Promise<void> = Promise.resolve();
  private backgroundRunning = false;
  private set background(run: Promise<void>) {
    this.backgroundRun = run;
    this.backgroundRunning = true;
    void run.finally(() => {
      if (this.backgroundRun === run) this.backgroundRunning = false;
    });
  }
  private get background(): Promise<void> {
    return this.backgroundRun;
  }
  /** Bytes each object downloaded as — what a load's progress reports. */
  private objectBytes = new Map<string, number>();

  private async fetchObject(url: string): Promise<AudioBuffer> {
    const ctx = this.ensureContext();
    const res = await audioFetch(url);
    if (!res.ok) throw new Error(`Could not load the clip (HTTP ${res.status}).`);
    const bytes = await res.arrayBuffer();
    // decodeAudioData detaches the buffer, so measure before decoding.
    this.objectBytes.set(url, bytes.byteLength);
    const buffer = await decodeObject(ctx, bytes);
    this.buffers.set(url, buffer);
    if (this.fromPreview.delete(url)) this.forgetPeaks(url);
    this.trimBuffers(url);
    this.layLanded(url);
    this.onDecoded?.();
    return buffer;
  }

  /**
   * Swap the material under a running transport — what an edit during
   * playback needs. Objects already decoded are simply rescheduled from the
   * current position (a move, a resize, a delete, a paste of a loaded clip:
   * no fetch at all); anything new is fetched first, the old schedule playing
   * on meanwhile, and the reschedule lands when it's in. A later call
   * supersedes an earlier one still loading.
   */
  async replaceSources(sources: ClipSource[], signal?: AbortSignal): Promise<{ failed: number }> {
    this.sources = sources;
    const generation = ++this.reloadGeneration;
    this.ensureContext();
    let failed = 0;
    await this.fetchEach(ArrangementPlayer.missingInPlayOrder(sources, this.buffers), signal, (_url, ok) => {
      if (!ok) failed++;
    });
    if (signal?.aborted) return { failed };
    if (generation !== this.reloadGeneration) return { failed };
    // The old schedule plays on while the new renders are made, then fades
    // into the new one at the playhead.
    await this.prepare(sources);
    if (generation !== this.reloadGeneration) return { failed };
    if (this.playing) await this.reschedule();
    else this.startOffset = Math.min(this.startOffset, this.duration);
    return { failed };
  }

  /** Whether this object has decoded — i.e. whether `peaks` can draw it. */
  hasBuffer(url: string): boolean {
    return this.buffers.has(url);
  }

  /**
   * Double-sided waveform overview of an object: `buckets` min/max pairs over
   * `[offsetSec, offsetSec + durationSec]`, interleaved `[min, max, …]`,
   * channels folded together (lib/waveform.ts). Read from the object's
   * summary, which outlives its decoded buffer; null until one of the two
   * exists — `ensurePeaks` fetches for a view the transport hasn't loaded.
   */
  peaks(url: string, buckets: number, offsetSec = 0, durationSec?: number): Float32Array | null {
    if (buckets <= 0) return null;
    const summary = this.summaryOf(url);
    if (!summary) return null;
    // Bucket counts are quantised to powers of two: every zoom step and
    // viewport width would otherwise mint its own array per clip, forever.
    // Rounded up, so a view never gets fewer buckets than it has pixels.
    buckets = 2 ** Math.max(3, Math.min(13, Math.ceil(Math.log2(buckets))));
    const span = durationSec ?? summary.length / summary.sampleRate - offsetSec;
    const key = `${url}|${offsetSec.toFixed(4)}|${span.toFixed(4)}|${buckets}`;
    const cached = this.peakCache.get(key);
    if (cached) return cached;
    const buffer = this.buffers.get(url);
    const out = envelope({ summary, raw: buffer ? channelsOf(buffer) : null }, buckets, offsetSec, span);
    const gain = normalGain(summary);
    for (let i = 0; i < out.length; i++) out[i]! *= gain;
    // Bounded: the oldest entries go first (a Map iterates in insertion order).
    if (this.peakCache.size >= PEAK_CACHE_MAX) {
      const oldest = this.peakCache.keys().next().value;
      if (oldest !== undefined) this.peakCache.delete(oldest);
    }
    this.peakCache.set(key, out);
    return out;
  }

  /** The object's min/max summary — built from its buffer on first ask. */
  private summaryOf(url: string): WaveSummary | null {
    const known = this.summaries.get(url);
    if (known) return known;
    const buffer = this.buffers.get(url);
    if (!buffer) return null;
    return this.keepSummary(url, summarize(channelsOf(buffer)));
  }

  private keepSummary(url: string, summary: WaveSummary): WaveSummary {
    if (this.summaries.size >= SUMMARY_CACHE_MAX) {
      const oldest = this.summaries.keys().next().value;
      if (oldest !== undefined) this.summaries.delete(oldest);
    }
    this.summaries.set(url, summary);
    return summary;
  }

  /**
   * Make `peaks` answer for an object nothing has loaded yet — a lane before
   * the first Play, a Session clip never launched. Decoded in the playback
   * context (created suspended: decoding needs no gesture, only sound does).
   * Queued, a few at a time; an object that failed is not asked for again.
   *
   * `preview` is the object's MP3 rendition, where it has one: drawn from
   * that, a room's waveforms cost ~100 KB an object instead of the
   * multi-megabyte PCM, and only what is actually played is downloaded whole.
   * The object decoding later — a Play, a launch — swaps in its exact summary
   * (an MP3's encoder delay shifts it by a few ms). Without a preview, or if
   * it won't load, the object itself is fetched and kept like any other
   * buffer, so the Play that follows fetches none of it again.
   *
   * `later` waits behind every `now`: a view that has something to show
   * (a stand-in drawn from another object) asks for the exact one with it,
   * so no waveform still missing waits on one that is merely refining.
   */
  ensurePeaks(url: string, priority: "now" | "later" = "now", preview?: string): void {
    if (this.disposed) return;
    if (preview !== undefined) this.peakPreviews.set(url, preview);
    if (this.summaries.has(url) || this.buffers.has(url) || this.peakFailed.has(url)) return;
    if (this.peakLoading.has(url) || this.peakQueue.includes(url)) return;
    const later = this.peakLaterQueue.indexOf(url);
    if (later >= 0) {
      if (priority === "later") return;
      this.peakLaterQueue.splice(later, 1);
    }
    (priority === "now" ? this.peakQueue : this.peakLaterQueue).push(url);
    this.pumpPeaks();
  }

  private pumpPeaks(): void {
    while (this.peakLoading.size < PEAK_FETCH_CONCURRENCY) {
      const url = this.peakQueue.shift() ?? this.peakLaterQueue.shift();
      if (url === undefined) break;
      if (this.summaries.has(url) || this.buffers.has(url)) continue;
      this.peakLoading.add(url);
      void this.fetchSummary(url)
        .then((summary) => {
          if (this.disposed) return;
          if (!this.summaries.has(url)) this.keepSummary(url, summary);
          this.onDecoded?.();
        })
        .catch((err: unknown) => {
          if (this.disposed) return;
          console.warn("[player] could not draw", url, err);
          this.peakFailed.add(url);
        })
        .finally(() => {
          this.peakLoading.delete(url);
          if (!this.disposed) this.pumpPeaks();
          if (this.peaksDrained()) for (const wake of this.peakIdleWaiters.splice(0)) wake();
        });
    }
  }

  /** Nothing drawing, nothing waiting to — or a closed player, which never will. */
  private peaksDrained(): boolean {
    if (this.peakLoading.size > 0) return false;
    return this.disposed || (this.peakQueue.length === 0 && this.peakLaterQueue.length === 0);
  }

  /** Resolves once every waveform asked for so far has been drawn (or failed). */
  private peaksIdle(): Promise<void> {
    if (this.peaksDrained()) return Promise.resolve();
    return new Promise((resolve) => this.peakIdleWaiters.push(resolve));
  }

  /** Idle, and still idle a moment later — views ask for their waveforms as
   * they mount, which can come a beat after the material does. */
  private async peaksSettled(): Promise<void> {
    do {
      await this.peaksIdle();
      await new Promise((resolve) => setTimeout(resolve, PEAKS_SETTLE_MS));
    } while (!this.peaksDrained());
  }

  /**
   * Fetch these sources' objects in the background, so the Play or launch
   * that follows finds them decoded. Waits for the waveforms first — drawn
   * from previews, they are what the view is waiting on — then goes earliest
   * on the timeline first, a few at a time, through `bufferFor`: a Play that
   * lands meanwhile joins what is in flight. A later call replaces this one.
   *
   * Stops at LARGE_LOAD_BYTES downloaded, or once decoded audio reaches the
   * buffer budget — past either, a page only opened to look would be paying
   * for a long arrangement, or trimming what it just fetched.
   */
  prefetch(sources: readonly ClipSource[]): void {
    if (this.disposed) return;
    this.prefetching?.abort();
    const controller = new AbortController();
    this.prefetching = controller;
    this.background = this.peaksSettled().then(() => {
      if (controller.signal.aborted) return;
      let bytes = 0;
      return this.fetchEach(ArrangementPlayer.missingInPlayOrder(sources, this.buffers), controller.signal, (url) => {
        bytes += this.objectBytes.get(url) ?? 0;
        let seconds = 0;
        for (const b of this.buffers.values()) seconds += b.duration;
        if (bytes >= LARGE_LOAD_BYTES || seconds >= ArrangementPlayer.MAX_BUFFER_SECONDS) controller.abort();
      });
    });
  }

  private async fetchSummary(url: string): Promise<WaveSummary> {
    const preview = this.peakPreviews.get(url);
    if (preview !== undefined && !this.decoding.has(url)) {
      try {
        const summary = await this.previewSummary(preview);
        // The object itself landed meanwhile: draw that instead.
        const buffer = this.buffers.get(url);
        if (buffer) return summarize(channelsOf(buffer));
        // Kept and announced here, as `fetchObject` does a buffer.
        this.fromPreview.add(url);
        this.keepSummary(url, summary);
        this.onDecoded?.();
        return summary;
      } catch (err) {
        console.warn("[player] no preview to draw, fetching the object", preview, err);
      }
    }
    const buffer = await this.bufferFor(url);
    return this.summaryOf(url) ?? summarize(channelsOf(buffer));
  }

  /** A preview's summary; its decoded PCM is not kept — nothing plays it. */
  private async previewSummary(url: string): Promise<WaveSummary> {
    const res = await audioFetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const decoded = await this.ensureContext().decodeAudioData(await res.arrayBuffer());
    return summarize(channelsOf(decoded));
  }

  /** Drop what was drawn for an object, so the next `peaks` redraws it. */
  private forgetPeaks(url: string): void {
    this.summaries.delete(url);
    for (const key of [...this.peakCache.keys()]) if (key.startsWith(`${url}|`)) this.peakCache.delete(key);
  }

  /** The lane's fader and pan, as the Set holds them — applied to playback so
   * the browser mix is the mix Live makes. */
  setLaneLevel(laneId: string, level: LaneLevel): void {
    this.levels.set(laneId, level);
    const nodes = this.lanes.get(laneId);
    if (nodes) this.applyLevel(nodes, level);
  }

  // ---- Session View launching ---------------------------------------------
  //
  // Live's other transport: one clip per track, launched on the bar, looping
  // until stopped or replaced. Playback-only — nothing here touches the wire.
  // Voices route through the lane's gate like timeline clips, so playback
  // mute/solo and the Set's fader apply; they live in their own map so the
  // timeline's reschedules never kill them and Stop kills everything.

  private slotVoices = new Map<
    string,
    {
      clipId: string;
      nodes: Array<AudioBufferSourceNode | OscillatorNode>;
      /** Context time the launch sounds from, and the clip's period — so a
       * piano roll can follow a launched clip round its loop. */
      startedAt: number;
      periodSec: number;
      /** The object a launched audio clip plays — kept decoded while it loops. */
      url?: string;
      /** What the launch was built from, its signature (`playbackSignature`)
       * and whether it loops — so an edit to the clip can swap it
       * (`refreshSlots`), and a render landing can (`followRenders`). */
      source: ClipSource | MidiSource;
      signature: string;
      loop: boolean;
      /** The launch's own gain into the lane: a swap fades it out under
       * the edited launch fading in. */
      bus: GainNode;
    }
  >();

  /** Where a lane's launched clip is in its own loop, in seconds from the
   * clip's start; null when nothing is launched there or the launch is
   * still waiting for its bar. */
  slotPositionSec(laneId: string): number | null {
    const v = this.slotVoices.get(laneId);
    if (!v || !this.ctx) return null;
    const elapsed = this.ctx.currentTime - v.startedAt;
    if (elapsed < 0 || v.periodSec <= 0) return null;
    return elapsed % v.periodSec;
  }

  /** The loop length of whatever is launched on a lane, in seconds; null
   * when nothing is. With `slotPositionSec` this is how far round the loop a
   * pad has got — and a launched lane whose position is null has not reached
   * its line yet, which is Live's queued clip. */
  slotPeriodSec(laneId: string): number | null {
    const v = this.slotVoices.get(laneId);
    return v && v.periodSec > 0 ? v.periodSec : null;
  }

  /** When a launched clip starts: on the next bar (Live's default), the next
   * beat, right away, or a custom number of beats. Playback-only. */
  launchQuantize: "bar" | "beat" | "none" | number = "bar";

  /** MIDI preview instrument per lane — playback-only, never on the wire. */
  private lanePatches = new Map<string, SynthPatch>();
  setLanePatch(laneId: string, patch: SynthPatch): void {
    this.lanePatches.set(laneId, patch);
  }
  private patchFor(laneId: string): SynthPatch {
    return this.lanePatches.get(laneId) ?? DEFAULT_PATCH;
  }

  /** Post-fader peak (0..1+) over the last window for one lane, for a meter.
   * 0 for a lane nothing has played through yet. */
  meterLevel(laneId: string): number {
    const nodes = this.lanes.get(laneId);
    return nodes ? this.peakOf(nodes.meter) : 0;
  }
  /** The master bus's peak, after the volume control. */
  masterLevel(): number {
    return this.masterMeter ? this.peakOf(this.masterMeter) : 0;
  }
  private peakOf(analyser: AnalyserNode): number {
    analyser.getFloatTimeDomainData(this.meterScratch);
    let peak = 0;
    for (let i = 0; i < this.meterScratch.length; i++) {
      const v = Math.abs(this.meterScratch[i] ?? 0);
      if (v > peak) peak = v;
    }
    return peak;
  }
  /** Fires with lane id → clip id for every lane with a launched clip. */
  onSessionChange: ((playing: Record<string, string>) => void) | null = null;
  /** Fires with the lanes the Session View has taken over (see
   * `overriddenLanes`) whenever that set changes. */
  onOverrideChange: ((lanes: Set<string>) => void) | null = null;

  /** Lanes playing (or stopped in) the Session View instead of their
   * timeline clips — the ones "Back to Arrangement" would hand back. */
  get overriddenLanes(): Set<string> {
    return new Set(this.overridden);
  }

  /** Take a lane for the Session View: its timeline material goes silent
   * (a running one mid-clip) and stays so until `backToArrangement`. */
  private override(laneIds: Iterable<string>): void {
    let changed = false;
    for (const laneId of laneIds) {
      if (this.overridden.has(laneId)) continue;
      this.overridden.add(laneId);
      changed = true;
      const nodes = this.lanes.get(laneId);
      if (nodes) nodes.arrangement.gain.value = 0;
    }
    if (changed) this.onOverrideChange?.(this.overriddenLanes);
  }

  /**
   * Live's "Back to Arrangement": the lane (or every lane) stops what the
   * Session View launched and plays its timeline clips again from wherever
   * the transport is — the voices were scheduled all along, only silenced,
   * so a clip the playhead is inside resumes part-way through. Immediate,
   * not quantized, as Live's is.
   */
  backToArrangement(laneId?: string): void {
    const laneIds = laneId === undefined ? [...this.overridden, ...this.slotVoices.keys()] : [laneId];
    let slotsChanged = false;
    let overrideChanged = false;
    for (const id of new Set(laneIds)) {
      if (this.slotVoices.has(id)) {
        this.killSlot(id);
        slotsChanged = true;
      }
      // A clip sounding out a queued stop on this lane goes now too: the
      // lane is the timeline's again, and two of them must not overlap.
      for (const r of this.retiring.filter((r) => r.laneId === id)) {
        this.killVoices(r.nodes);
        r.bus.disconnect();
      }
      this.retiring = this.retiring.filter((r) => r.laneId !== id);
      if (this.overridden.delete(id)) {
        overrideChanged = true;
        const nodes = this.lanes.get(id);
        if (nodes) nodes.arrangement.gain.value = 1;
      }
    }
    this.syncSessionClock();
    if (slotsChanged) this.onSessionChange?.(this.sessionPlaying);
    if (overrideChanged) this.onOverrideChange?.(this.overriddenLanes);
  }

  /** Beats in one launch quantum — a bar, a beat, a custom number, or none. */
  private launchQuantumBeats(): number {
    if (typeof this.launchQuantize === "number") return this.launchQuantize;
    return this.launchQuantize === "bar" ? beatsPerBar(this.meter) : beatUnit(this.meter);
  }

  /**
   * When the next thing launched should sound: the next line of whichever
   * clock is running — the transport's while it plays, the Session View's
   * while clips loop without it — or right away when neither is.
   */
  private nextGridLine(quantumBeats: number): number {
    const ctx = this.ensureContext();
    if (this.playing) {
      // The transport itself waiting for a bar line: that line is the grid.
      if (ctx.currentTime < this.startedAt) return this.startedAt;
      const at = this.position;
      return ctx.currentTime + Math.max(0, nextBarStart(at, this.tempo, quantumBeats) - at);
    }
    if (this.sessionClockAt !== null) {
      const origin = this.sessionClockAt;
      return Math.max(ctx.currentTime, origin + nextBarStart(ctx.currentTime - origin, this.tempo, quantumBeats));
    }
    return ctx.currentTime + 0.02;
  }

  /** The first launch with the transport stopped: from here, everything is
   * in time with it. */
  private startSessionClock(at: number): void {
    this.sessionClockAt = at;
    this.scheduleSessionClicks();
  }

  /** The grid is over — the last clip stopped, or the transport took it.
   * Clicks already sounded are left alone; the ones from `from` on go. */
  private endSessionClock(from = 0): void {
    this.sessionClockAt = null;
    const kept: Array<{ node: OscillatorNode; at: number }> = [];
    for (const click of this.sessionClicks) {
      if (click.at < from) {
        kept.push(click);
        continue;
      }
      try {
        click.node.stop();
        click.node.disconnect();
      } catch {
        // never started, or already finished
      }
    }
    this.sessionClicks = kept;
  }

  /** The Session View is playing on its own again after the transport had
   * the grid: the bar lines carry on where the transport left them, so a
   * clip launched now is still in time with the loops that never stopped. */
  private adoptTransportGrid(at: number): void {
    const ctx = this.ctx;
    if (!ctx || this.slotVoices.size === 0 || !(this.tempo > 0)) return;
    const bar = (60 / this.tempo) * beatsPerBar(this.meter);
    this.startSessionClock(ctx.currentTime - (at - Math.floor(at / bar) * bar));
  }

  /** How far ahead the Session View's click is scheduled. Nothing refills
   * it on a timer: every launch and every stop reschedules from now, and a
   * grid left running longer than this had nobody listening for its bar. */
  private static readonly SESSION_CLICK_HORIZON_SEC = 600;

  /** The click on the Session View's grid, from now to the horizon. */
  private scheduleSessionClicks(): void {
    const origin = this.sessionClockAt;
    if (origin === null) return;
    this.endSessionClockClicks();
    if (!this.clicking || !(this.tempo > 0)) return;
    const ctx = this.ensureContext();
    const beat = (60 / this.tempo) * beatUnit(this.meter);
    const first = Math.max(0, Math.ceil((ctx.currentTime - origin) / beat - 1e-6));
    const last = first + Math.ceil(ArrangementPlayer.SESSION_CLICK_HORIZON_SEC / beat);
    for (let i = first; i < last && this.sessionClicks.length < ArrangementPlayer.MAX_CLICKS; i++) {
      const at = origin + i * beat;
      this.sessionClicks.push({ node: this.click(at, i % this.meter.numerator === 0, "session"), at });
    }
  }

  /** Silence the scheduled click without ending the grid. */
  private endSessionClockClicks(): void {
    for (const { node } of this.sessionClicks) {
      try {
        node.stop();
        node.disconnect();
      } catch {
        // never started, or already finished
      }
    }
    this.sessionClicks = [];
  }

  /** The grid lasts exactly as long as the Session View plays on its own:
   * called wherever a launch ends, it ends with the last one. A clip whose
   * stop is QUEUED for a later bar is still playing, so it still holds the
   * grid — launch something while a row is stopping and it lands on the same
   * lines, as it does in Live. */
  private syncSessionClock(): void {
    if (this.slotVoices.size > 0 || this.retiringVoices() || this.playing) return;
    this.endSessionClock(this.ctx?.currentTime ?? 0);
  }

  /** Whether any released voice is still sounding out its queued stop. */
  private retiringVoices(): boolean {
    const now = this.ctx?.currentTime ?? 0;
    return this.retiring.some((r) => r.until > now);
  }

  /** Every lane the player knows of — with nodes, or with timeline
   * material waiting to be scheduled. */
  private knownLanes(): Set<string> {
    const out = new Set(this.lanes.keys());
    for (const s of this.sources) out.add(s.laneId);
    for (const s of this.midi) out.add(s.laneId);
    return out;
  }
  /** How many times a launched MIDI clip repeats before it runs out (audio
   * loops natively). At one bar a clip this is ~8 minutes at 120. */
  private static readonly MIDI_LAUNCH_LOOPS = 256;
  /** Voices one looping MIDI clip may have scheduled and not yet ended. */
  private static readonly MAX_LAUNCH_VOICES = 4000;
  /** How far ahead of the clock a looping MIDI clip's passes are laid. Every
   * voice is a dozen nodes the audio thread walks each quantum from the
   * moment it is built, started or not: all of a launch's passes up front
   * (a 4-note clip is ~1000 voices) starved the render and the pad blipped
   * once, then went quiet. A timer tops this up; a background tab's 1 s
   * timer clamp stays well inside it. */
  private static readonly MIDI_AHEAD_SEC = 4;
  private static readonly MIDI_FEED_MS = 250;

  /** Looping MIDI being laid pass by pass (`feedMidi`). */
  private midiFeeds = new Set<MidiFeed>();
  private midiFeedTimer: ReturnType<typeof setInterval> | null = null;
  /** Voices whose oscillator has ended — pruned from a feed's list so it
   * holds only what still sounds or is still to. */
  private endedVoices = new WeakSet<AudioScheduledSourceNode>();

  /**
   * Lay a MIDI clip's passes from `feed.next` up to `MIDI_AHEAD_SEC` past
   * now, then keep doing so on a timer until every pass is laid (`done`
   * gets the voice that ends last) or the clip is no longer wanted (`live`).
   * Voices go into `feed.nodes` — the launch's or the audition's own list —
   * so whatever stops those stops the passes laid so far, and the feed
   * notices the list is no longer current and stops laying more.
   */
  private feedMidi(feed: MidiFeed): void {
    if (!this.layMidi(feed)) return;
    this.midiFeeds.add(feed);
    if (this.midiFeedTimer === null) {
      this.midiFeedTimer = globalThis.setInterval(() => {
        for (const f of this.midiFeeds) if (!this.layMidi(f)) this.midiFeeds.delete(f);
        if (this.midiFeeds.size === 0 && this.midiFeedTimer !== null) {
          globalThis.clearInterval(this.midiFeedTimer);
          this.midiFeedTimer = null;
        }
      }, ArrangementPlayer.MIDI_FEED_MS);
    }
  }

  /** One top-up of a feed; true while it has passes still to lay. */
  private layMidi(feed: MidiFeed): boolean {
    if (this.disposed || !feed.live()) return false;
    const ctx = this.ensureContext();
    const horizon = ctx.currentTime + ArrangementPlayer.MIDI_AHEAD_SEC;
    for (let i = feed.nodes.length - 1; i >= 0; i--) if (this.endedVoices.has(feed.nodes[i]!)) feed.nodes.splice(i, 1);
    // Always the first pass whatever its start: the launch must be on the
    // clock the moment it is made, not a timer tick later.
    while (feed.next < feed.passes && (feed.next === 0 || feed.base + feed.next * feed.period < horizon)) {
      const i = feed.next++;
      const passNotes = i === 0 ? feed.src.notes : (feed.src.reroll?.() ?? feed.src.notes);
      for (const n of passNotes) {
        if (n.durationSec <= 0) continue;
        if (feed.nodes.length >= ArrangementPlayer.MAX_LAUNCH_VOICES) break;
        const at = i * feed.period + n.startSec;
        if (at < feed.phase - 1e-6) continue;
        const osc = this.startTone(n, feed.base + at, feed.out, feed.patch);
        osc.addEventListener("ended", () => this.endedVoices.add(osc));
        feed.nodes.push(osc);
        if (at + n.durationSec > feed.lastEnd) {
          feed.lastEnd = at + n.durationSec;
          feed.last = osc;
        }
      }
    }
    if (feed.next < feed.passes) return true;
    // A last voice already over (a pass laid as it played) ends nothing.
    feed.done(feed.last && !this.endedVoices.has(feed.last) ? feed.last : null);
    return false;
  }

  /** What a scene launch managed: a clip whose object would not load leaves
   * its lane silent rather than the row dead, so the two are counted. */
  // (declared beside the getter it reads with, not in a types file: nothing
  // outside the player and its hook has any use for it.)
  /**
   * Lane id → clip id of everything whose stop is QUEUED: still sounding,
   * already told to stop on a coming line. Live keeps such a clip lit with
   * its stop blinking, and tapping it again takes the lane back — which
   * falls out of `sessionPlaying` no longer holding it.
   */
  get sessionStopping(): Record<string, string> {
    const now = this.ctx?.currentTime ?? 0;
    const out: Record<string, string> = {};
    for (const r of this.retiring) {
      if (r.until > now && !this.slotVoices.has(r.laneId)) out[r.laneId] = r.clipId;
    }
    return out;
  }

  /** Lane id → clip id of everything launched. */
  get sessionPlaying(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [laneId, v] of this.slotVoices) out[laneId] = v.clipId;
    return out;
  }

  /**
   * Launch a slot clip on its lane, replacing whatever the lane was playing
   * — one clip per lane, every lane at once, as Live's grid plays. The lane's
   * timeline clips fall silent (the Session View has taken the lane) until
   * `backToArrangement`. Starts on the next bar line of a running transport
   * (Live's default launch quantization), otherwise right away. Audio loops
   * over its object; MIDI repeats its notes for a long while. `loop: false`
   * plays once.
   */
  async launchSlot(
    laneId: string,
    clipId: string,
    src: ClipSource | MidiSource,
    loop = true,
  ): Promise<number | null> {
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") await ctx.resume();
    if (!("notes" in src)) {
      await this.bufferFor(src.url);
      await this.prepare([src]);
    }
    const at = this.startSlot(laneId, clipId, src, loop, this.nextLaunchTime());
    this.onSessionChange?.(this.sessionPlaying);
    return at;
  }

  /**
   * Live's scene launch: every clip in the row starts and every lane the row
   * leaves empty stops — all on ONE grid line.
   *
   * One line is the point. Launching each clip and stopping each lane
   * separately computes the next bar separately, and a scene taken across a
   * bar boundary then splits in two: half the row on this bar, half on the
   * next, with a hole where the stopped lanes fell silent early. The row is
   * a state of the whole Set, so it changes at one instant.
   */
  async launchScene(
    items: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>,
    stopLaneIds: readonly string[] = [],
  ): Promise<SceneLaunchResult> {
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") await ctx.resume();
    // Decode first, THEN pick the line: a fetch that outlived the bar would
    // otherwise drag its lane past the one the rest of the row landed on.
    // One object that will not load is one silent lane, not a dead row — the
    // rest of the scene still goes.
    const urls = new Set(items.map((i) => ("notes" in i.source ? null : i.source.url)).filter((u): u is string => u !== null));
    await Promise.all(
      [...urls].map((u) =>
        this.bufferFor(u).catch((err: unknown) => {
          console.warn("[player] scene launch could not load", u, err);
          return null;
        }),
      ),
    );

    await this.prepare(items.flatMap((i) => ("notes" in i.source ? [] : [i.source])));
    const when = this.nextLaunchTime();
    const result: SceneLaunchResult = { started: 0, failed: 0, atBeat: null, launched: [] };
    let changed = false;
    for (const item of items) {
      const atBeat = this.startSlot(item.laneId, item.clipId, item.source, item.loop ?? true, when);
      if (atBeat === null) result.failed++;
      else {
        result.started++;
        result.atBeat = atBeat;
        result.launched.push({ laneId: item.laneId, clipId: item.clipId });
      }
      changed = true;
    }
    for (const laneId of stopLaneIds) {
      if (this.releaseSlot(laneId, when)) changed = true;
      this.override([laneId]);
    }
    this.syncSessionClock();
    if (changed) this.onSessionChange?.(this.sessionPlaying);
    return result;
  }

  /**
   * An edit reaches what is launched: for every lane whose launched clip is
   * one of `items` but no longer sounds like its source (a transpose, a
   * brace, a warp marker, new notes — `playbackSignature`), the launch is
   * swapped for the edited one at the phase it has reached, crossfaded
   * (`RESCHEDULE_XFADE_SEC`). Lanes whose launch is unchanged are left
   * alone; so is a lane launched on another clip meanwhile.
   */
  async refreshSlots(
    items: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>,
  ): Promise<void> {
    const stale = items.filter((i) => {
      const v = this.slotVoices.get(i.laneId);
      return v !== undefined && v.clipId === i.clipId && v.signature !== playbackSignature(i.source);
    });
    if (stale.length === 0) return;
    await this.swapSlots(stale);
  }

  /** The swap itself, for launches whose source is known to have changed
   * (or whose exact render just landed, `followRenders`). */
  private async swapSlots(
    stale: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>,
  ): Promise<void> {
    const audio = stale.flatMap((i) => ("notes" in i.source ? [] : [i.source]));
    await Promise.all(
      audio.map((s) =>
        this.bufferFor(s.url).catch((err: unknown) => {
          console.warn("[player] refresh could not load", s.url, err);
          return null;
        }),
      ),
    );
    await this.prepare(audio);
    // Not on the grid: at the phase each launch has reached, a lead from
    // now, the old fading out under the new — an edit is heard as a change
    // in the pad that is playing, never as the pad starting over.
    const ctx = this.ensureContext();
    const when = ctx.currentTime + TRANSPORT_LEAD_SEC;
    let changed = false;
    for (const i of stale) {
      const v = this.slotVoices.get(i.laneId);
      if (!v || v.clipId !== i.clipId) continue; // relaunched or stopped meanwhile
      if (v.signature === playbackSignature(i.source) && v.source !== i.source) continue; // caught up by a launch (a render landing swaps the same source)
      const phaseSec = Math.max(0, when - v.startedAt);
      this.startSlot(i.laneId, i.clipId, i.source, i.loop ?? v.loop, when, { phaseSec });
      changed = true;
    }
    if (changed) this.onSessionChange?.(this.sessionPlaying);
  }

  /** When the next launch (or the next stop) lands: the grid's line under
   * the current quantization, or as good as now when there is no grid. */
  private nextLaunchTime(): number {
    const ctx = this.ensureContext();
    return this.tempo > 0 && this.launchQuantize !== "none"
      ? this.nextGridLine(this.launchQuantumBeats())
      : ctx.currentTime + 0.02;
  }

  /** One launch, at a line the caller has already chosen. */
  private startSlot(
    laneId: string,
    clipId: string,
    src: ClipSource | MidiSource,
    loop: boolean,
    when: number,
    /** A swap for an edited source (`refreshSlots`): pick the clip up
     * `phaseSec` room seconds into it, fading in over the old launch. */
    swap?: { phaseSec: number },
  ): number | null {
    const ctx = this.ensureContext();
    const isMidi = "notes" in src;

    // The clip the lane was on plays UP TO the new one rather than being cut
    // the moment the pad is hit: Live swaps clips on the line, and a kill
    // here would leave the lane silent for the rest of the bar. A swap fades
    // it out under the new one instead.
    this.releaseSlot(laneId, when, swap !== undefined);
    this.override([laneId]);
    // Nothing was running to be in time with: this launch IS the grid from
    // here, and the click (if it is on) starts with it.
    if (!this.playing && this.sessionClockAt === null) this.startSessionClock(when);
    const bus = ctx.createGain();
    bus.connect(this.laneGain(laneId));
    if (swap) {
      bus.gain.setValueAtTime(0, when);
      bus.gain.linearRampToValueAtTime(1, when + RESCHEDULE_XFADE_SEC);
    }
    const out = bus;
    const nodes: Array<AudioBufferSourceNode | OscillatorNode> = [];
    let periodSec = 0;
    let phase = 0;
    let feed: MidiFeed | null = null;

    if (!isMidi) {
      const resolved = this.resolve(src);
      if (!resolved) return null;
      const { buffer, offset, rate, scale } = resolved;
      const length = Math.max(0.01, resolved.playFor);
      const node = ctx.createBufferSource();
      node.buffer = buffer;
      node.playbackRate.value = rate;
      node.connect(out);
      if (loop) {
        node.loop = true;
        // The brace when the clip has one (head first, then round the
        // brace, as Live launches it); the whole object otherwise.
        const braceStart =
          src.loopStartSec !== undefined ? Math.max(0, Math.min(src.loopStartSec * scale, buffer.duration)) : offset;
        const braceEnd =
          src.loopEndSec !== undefined
            ? Math.max(braceStart + 0.01, Math.min(src.loopEndSec * scale, buffer.duration))
            : offset + length;
        node.loopStart = braceStart;
        node.loopEnd = braceEnd;
        // Where in the object the launch is `phaseSec` in: the head, then
        // round the brace.
        phase = swap?.phaseSec ?? 0;
        let from = offset + phase * rate;
        if (from >= braceEnd) from = braceStart + ((from - braceEnd) % Math.max(braceEnd - braceStart, 1e-6));
        node.start(when, from);
      } else {
        phase = Math.min(swap?.phaseSec ?? 0, length / rate);
        node.start(when, offset + phase * rate, Math.max(0.001, length - phase * rate));
        node.addEventListener("ended", () => this.slotEnded(laneId, nodes));
      }
      periodSec = src.durationSec ?? length / rate;
      nodes.push(node);
    } else {
      const period = Math.max(src.durationSec, 0.01);
      periodSec = period;
      const loops = loop ? ArrangementPlayer.MIDI_LAUNCH_LOOPS : 1;
      // A swap lays the first pass from the phase the old launch is at.
      phase = swap ? (loop ? swap.phaseSec % period : Math.min(swap.phaseSec, period)) : 0;
      const base = when - phase;
      feed = {
        src,
        out,
        patch: this.patchFor(laneId),
        base,
        period,
        phase,
        passes: loops,
        next: 0,
        nodes,
        last: null,
        lastEnd: -Infinity,
        live: () => this.slotVoices.get(laneId)?.nodes === nodes,
        done: (last) => {
          if (last) {
            last.addEventListener("ended", () => this.slotEnded(laneId, nodes));
            return;
          }
          // Every pass rolled silent (a clip of chance notes): the launch
          // still occupies its passes, then ends, instead of showing as
          // launched forever with nothing to end it.
          const passesSec = Math.max(0, base + period * loops - ctx.currentTime);
          window.setTimeout(() => this.slotEnded(laneId, nodes), passesSec * 1000);
        },
      };
    }

    this.slotVoices.set(laneId, {
      clipId,
      nodes,
      // A swap's launch started, as far as the loop is concerned, a phase
      // ago: the pad's position reads continuously across it.
      startedAt: when - phase,
      periodSec,
      source: src,
      signature: playbackSignature(src),
      loop,
      bus,
      ...(isMidi ? {} : { url: src.url }),
    });
    // Laid once the launch is the lane's: the feed lays only while it is.
    if (feed) this.feedMidi(feed);
    this.applyGains();
    // The beat this launch sounds at — what a session recording spans from.
    const launchPositionSec = this.playing ? this.position + Math.max(0, when - ctx.currentTime) : this.position;
    return (launchPositionSec * this.tempo) / 60;
  }

  /** A launch ran out on its own. Matched by the VOICES it started, not the
   * clip id: relaunching the same clip replaces the entry, and the old
   * launch's late "ended" must not delete the new one (whose voices would
   * then play on with nothing able to stop them). */
  private slotEnded(laneId: string, nodes: Array<AudioBufferSourceNode | OscillatorNode>): void {
    const v = this.slotVoices.get(laneId);
    if (!v || v.nodes !== nodes) return;
    this.slotVoices.delete(laneId);
    this.syncSessionClock();
    this.onSessionChange?.(this.sessionPlaying);
  }

  /**
   * The lane's clip stop button: its launched clip stops, and the lane stays
   * the Session View's — silent, its timeline clips NOT resumed — until
   * `backToArrangement`, exactly as Live's ■ leaves a track. Pressed on a
   * lane with nothing launched it still takes the lane (Live's stop button
   * in an empty slot silences the track's arrangement).
   */
  stopSlot(laneId: string, notify = true): void {
    const had = this.releaseSlot(laneId, this.nextLaunchTime());
    this.override([laneId]);
    this.syncSessionClock();
    if (had && notify) this.onSessionChange?.(this.sessionPlaying);
  }

  /** The master's Stop All Clips: every launch stops and every lane is
   * the Session View's until "Back to Arrangement". One grid line for the
   * lot, so the Set stops square rather than lane by lane. */
  stopAllSlots(): void {
    const when = this.nextLaunchTime();
    let had = false;
    for (const laneId of [...this.slotVoices.keys()]) if (this.releaseSlot(laneId, when)) had = true;
    this.override(this.knownLanes());
    this.syncSessionClock();
    if (had) this.onSessionChange?.(this.sessionPlaying);
  }

  /**
   * A lane's launch, let go of at `at` rather than cut off now.
   *
   * Live quantizes a stop exactly as it quantizes a launch: the clip plays
   * out to the line and stops there. The voices leave `slotVoices` at once —
   * the lane's clip is no longer what it will be playing, and the grid must
   * show that the moment the pad is hit — but they keep the grid alive
   * (`retiringVoices`) until they actually fall silent.
   *
   * `node.stop(at)` can only shorten what is scheduled: a looping object
   * runs until told to stop, a one-shot is already capped by its duration,
   * and a synth voice is silent past its own release envelope whatever its
   * oscillator does. True when there was something to let go of.
   */
  private releaseSlot(laneId: string, at: number, fade = false): boolean {
    const v = this.slotVoices.get(laneId);
    if (!v) return false;
    this.slotVoices.delete(laneId);
    const now = this.ctx?.currentTime ?? 0;
    if (!(at > now + 1e-4)) {
      this.killVoices(v.nodes);
      v.bus.disconnect();
      return true;
    }
    if (fade) {
      // Under the launch replacing it: out over the crossfade, gone just after.
      v.bus.gain.setValueAtTime(1, at);
      v.bus.gain.linearRampToValueAtTime(0, at + RESCHEDULE_XFADE_SEC);
      at += RESCHEDULE_XFADE_SEC + 0.005;
    }
    for (const node of v.nodes) stopVoice(node, at);
    const entry = { laneId, clipId: v.clipId, nodes: v.nodes, bus: v.bus, until: at };
    this.retiring.push(entry);
    window.setTimeout(
      () => {
        if (!this.retiring.includes(entry)) return;
        this.retiring = this.retiring.filter((r) => r !== entry);
        this.killVoices(entry.nodes);
        entry.bus.disconnect();
        this.syncSessionClock();
        // The lane has actually fallen silent now: the grid stops drawing it
        // as stopping.
        this.onSessionChange?.(this.sessionPlaying);
      },
      (at - now) * 1000 + 50,
    );
    return true;
  }

  /** Voices let go of at a future line, still sounding it out. */
  private retiring: Array<{
    laneId: string;
    clipId: string;
    nodes: Array<AudioBufferSourceNode | OscillatorNode>;
    bus: GainNode;
    until: number;
  }> = [];

  private killVoices(nodes: Array<AudioBufferSourceNode | OscillatorNode>): void {
    for (const node of nodes) {
      try {
        stopVoice(node);
        node.disconnect();
      } catch {
        // never started, or already finished
      }
    }
  }

  /** Silence a lane's launched voices without touching the override; true
   * when there was something to stop. */
  private killSlot(laneId: string): boolean {
    const v = this.slotVoices.get(laneId);
    if (!v) return false;
    this.killVoices(v.nodes);
    v.bus.disconnect();
    this.slotVoices.delete(laneId);
    return true;
  }

  /** Every launch silenced, overrides kept — the transport's Stop: Live's
   * "Back to Arrangement" stays lit through a stop. A clip sounding out a
   * queued stop goes with them: Stop means now. */
  private killSlots(): boolean {
    const had = this.slotVoices.size > 0 || this.retiring.length > 0;
    for (const laneId of [...this.slotVoices.keys()]) this.killSlot(laneId);
    for (const r of this.retiring) {
      this.killVoices(r.nodes);
      r.bus.disconnect();
    }
    this.retiring = [];
    return had;
  }

  /**
   * Render the whole arrangement offline — the same schedule `play()` makes,
   * through the same per-lane gate → level → pan chain, into a buffer — for
   * a mixdown download. Objects not yet decoded are fetched first. Nothing
   * live is disturbed: the render happens on its own OfflineAudioContext.
   */
  /**
   * Sources rendered on their own and dry, exactly as they play (warp,
   * markers, pitch, loop passes) but with no lane chain, fader, send or
   * master: the clip editor's "Bounce to new clip". `startSec` is measured
   * from the render's start; the result is stereo at the playback rate.
   */
  async renderSources(sources: readonly ClipSource[], durationSec: number): Promise<AudioBuffer> {
    for (const url of new Set(sources.map((s) => s.url))) await this.bufferFor(url);
    await this.prepare(sources, true);
    const rate = this.ensureContext().sampleRate;
    const offline = new OfflineAudioContext(2, Math.max(1, Math.ceil(durationSec * rate)), rate);
    for (const src of sources) {
      const resolved = this.resolve(src);
      if (!resolved) continue;
      const { buffer, offset, playFor, rate: playbackRate, outSec } = resolved;
      const node = offline.createBufferSource();
      node.buffer = buffer;
      node.playbackRate.value = playbackRate;
      node.connect(declick(offline, offline.destination, src.startSec, outSec));
      node.start(src.startSec, offset, playFor);
    }
    return offline.startRendering();
  }

  async renderMixdown(
    sources: ClipSource[],
    onProgress?: (p: LoadProgress) => void,
    laneFilter?: (laneId: string) => boolean,
    /** Apply the browser's playback mute/solo. A stem export passes false:
     * a lane soloed for listening must not silence every other stem. A saved
     * master passes "mutes": a muted lane is silent, a solo left on is not a
     * mix. */
    respectGates: boolean | "mutes" = true,
    /** Render the lanes' CONTENT: unity fader, centre pan, no sends — what
     * a consolidation wants, since the result plays through the lane's
     * fader again. */
    preFader = false,
  ): Promise<AudioBuffer> {
    if (laneFilter) sources = sources.filter((s) => laneFilter(s.laneId));
    let midi = laneFilter ? this.midi.filter((s) => laneFilter(s.laneId)) : this.midi;
    // A saved master ends where its sound does: a muted lane is left out, not
    // rendered at zero, or its clips would pad the file with silence.
    if (respectGates === "mutes") {
      sources = sources.filter((s) => !this.muted.has(s.laneId));
      midi = midi.filter((s) => !this.muted.has(s.laneId));
    }
    const urls = [...new Set(sources.map((s) => s.url))];
    const progress: LoadProgress = { loaded: 0, total: urls.length, bytes: 0, failed: 0 };
    for (const url of urls) {
      try {
        await this.bufferFor(url);
      } catch (err) {
        console.warn("[player] mixdown skipping", url, err);
        progress.failed++;
      }
      progress.loaded++;
      onProgress?.({ ...progress });
    }
    await this.prepare(sources, true);
    const live = this.ensureContext();
    const rate = live.sampleRate;
    const duration = Math.max(
      sources.reduce((m, s) => Math.max(m, s.startSec + this.lengthOf(s)), 0),
      midi.reduce((m, s) => Math.max(m, s.startSec + s.durationSec), 0),
      0.1,
    );
    const offline = new OfflineAudioContext(2, Math.ceil(duration * rate), rate);
    // The Set's master fader + pan on the mixdown too; stems (a lane filter)
    // are pre-master, as Live's are.
    const limiter = masterLimiter(offline);
    const bus = offline.createGain();
    // Where the lanes and returns sum. On the mixdown the master's chain
    // sits between the sum and the fader, as in Live; stems are pre-master
    // and skip it.
    let sum: GainNode = bus;
    if (!laneFilter) {
      sum = offline.createGain();
      const chain = buildGraph(offline, this.stagesOf(this.masterStages));
      sum.connect(chain.input);
      chain.output.connect(bus);
      bus.gain.value = faderToGain(this.masterFader.volume);
      const masterPan = createPanner(offline, this.masterFader.pan);
      bus.connect(masterPan.input);
      masterPan.output.connect(limiter);
    } else {
      bus.connect(limiter);
    }
    limiter.connect(offline.destination);
    const lanes = new Map<string, GainNode>();
    const soloing = respectGates === true && this.soloed.size > 0;
    // Return buses on the mixdown (not on stems — a stem is one lane, dry).
    // A soloed return closes the lanes' direct paths on the mixdown as on
    // the live bus — but not on a stem (no return bus exists there, and a
    // stem ignores the listening gates by contract).
    const returnSolo = !laneFilter && respectGates === true && this.returnLevels.some((r) => r.solo);
    const returnInputs: GainNode[] = laneFilter
      ? []
      : this.returnLevels.map((r, i) => {
          const input = offline.createGain();
          const level = offline.createGain();
          const pan = createPanner(offline, r.pan);
          level.gain.value = isAudible(r.mute, r.solo, returnSolo) ? faderToGain(r.volume) : 0;
          const chain = buildGraph(offline, this.stagesOf(this.returnStages[i]));
          input.connect(chain.input);
          chain.output.connect(level);
          level.connect(pan.input);
          pan.output.connect(sum);
          return input;
        });
    const laneIn = (laneId: string): GainNode => {
      let gate = lanes.get(laneId);
      if (!gate) {
        gate = offline.createGain();
        const audible = !respectGates || isAudible(this.muted.has(laneId), this.soloed.has(laneId), soloing);
        gate.gain.value = audible ? 1 : 0;
        const level = offline.createGain();
        const stored = preFader ? undefined : this.levels.get(laneId);
        const pan = createPanner(offline, stored ? stored.pan : CENTER_PAN);
        level.gain.value = stored ? faderToGain(clampVolume(stored.volume)) : 1;
        const direct = offline.createGain();
        direct.gain.value = returnSolo && !this.soloed.has(laneId) ? 0 : 1;
        // The lane's chain: on a mixdown and a stem (Live's stems are
        // post-effects), never on a content render.
        const chain = buildGraph(offline, preFader ? [] : this.stagesOf(this.laneStages.get(laneId)));
        gate.connect(chain.input);
        chain.output.connect(level);
        level.connect(pan.input);
        pan.output.connect(direct);
        direct.connect(sum);
        const sends = preFader ? [] : (this.laneSends.get(laneId) ?? []);
        returnInputs.forEach((input, i) => {
          const send = sends[i] ?? 0;
          if (send <= 0) return;
          const tap = offline.createGain();
          tap.gain.value = sendToGain(send);
          pan.output.connect(tap);
          tap.connect(input);
        });
        lanes.set(laneId, gate);
      }
      return gate;
    };
    for (const src of sources) {
      const resolved = this.resolve(src);
      if (!resolved) continue;
      const { buffer, offset, playFor, rate, outSec } = resolved;
      const node = offline.createBufferSource();
      node.buffer = buffer;
      node.playbackRate.value = rate;
      node.connect(declick(offline, laneIn(src.laneId), src.startSec, outSec));
      node.start(src.startSec, offset, playFor);
    }
    for (const src of midi) {
      for (const n of src.notes) {
        if (n.durationSec <= 0) continue;
        scheduleTone(offline, n, src.startSec + n.startSec, laneIn(src.laneId), this.patchFor(src.laneId));
      }
    }
    return offline.startRendering();
  }

  /** A quantized jump in flight: the run that plays up to the line. */
  private jumpFrom: { offset: number; startedAt: number; until: number } | null = null;

  /** Where the transport stops by itself (Live's "play the selection"):
   * seconds, or null to play on. Cleared when it is reached. */
  stopAt: number | null = null;

  /**
   * Live's scrub-strip click while playing: the transport jumps to `to` on
   * the next launch-quantization line, so the music stays in time, and what
   * was playing plays up to that line. With no quantization, or stopped, it
   * is a plain seek.
   */
  async jumpQuantized(to: number): Promise<void> {
    const target = Math.max(0, Math.min(to, this.duration));
    if (!this.playing) {
      this.startOffset = target;
      return;
    }
    if (this.launchQuantize === "none" || !(this.tempo > 0)) {
      await this.play(target);
      return;
    }
    await this.prepare(this.sources);
    if (!this.playing) return;
    const when = this.nextGridLine(this.launchQuantumBeats());
    // Everything scheduled so far ends on the line (a voice due after it
    // never starts); the new run begins there.
    for (const { node } of this.voices) stopVoice(node, when);
    this.nextWrap = null;
    this.jumpFrom = { offset: this.startOffset, startedAt: this.startedAt, until: when };
    this.startOffset = target;
    this.startedAt = when;
    this.schedule(when);
  }

  async play(from?: number): Promise<void> {
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") await ctx.resume();
    this.jumpFrom = null;
    // An edit's render may still be on the stretch Worker (Space pressed
    // right after a marker drag): wait for it, so what plays is the edit and
    // not a main-thread render of it. Instant when everything is ready.
    await this.prepare(this.sources);

    const at = from ?? this.position;
    this.stopVoices();
    this.startOffset = at >= this.duration ? 0 : at;
    // Clips already looping: the arrangement comes in on THEIR bar line, not
    // wherever the mouse landed — the click is already going, and this is
    // what lines up with it. From there the transport is the grid, so its own
    // click takes over at the same line and the Session View's stops there.
    const lead = ctx.currentTime + TRANSPORT_LEAD_SEC;
    const startAt =
      this.sessionClockAt !== null && this.tempo > 0 && this.launchQuantize !== "none"
        ? Math.max(lead, this.nextGridLine(beatsPerBar(this.meter)))
        : lead;
    this.endSessionClock(startAt);
    this.startedAt = startAt;
    this.playing = true;
    this.schedule(startAt);
  }

  pause(): void {
    if (!this.playing) return;
    const at = this.position;
    this.jumpFrom = null;
    this.stopVoices();
    this.playing = false;
    this.startOffset = Math.min(at, this.duration);
    this.adoptTransportGrid(at);
  }

  seek(to: number): void {
    const at = Math.max(0, Math.min(to, this.duration));
    if (this.playing) {
      void this.play(at);
    } else {
      this.startOffset = at;
    }
  }

  /** Stop everything. What the Session View took stays taken (Live keeps
   * "Back to Arrangement" lit through a stop), so the next Play hears the
   * arrangement on the lanes it never took. */
  stop(): void {
    this.stopVoices();
    if (this.killSlots()) this.onSessionChange?.(this.sessionPlaying);
    this.playing = false;
    this.startOffset = 0;
    this.syncSessionClock();
  }

  /** Stop the arrangement transport only — a launched Session clip keeps
   * looping (a changed arrangement before Play was ever pressed is no
   * reason to silence the Session View). */
  stopArrangement(): void {
    const at = this.position;
    this.stopVoices();
    this.playing = false;
    this.startOffset = 0;
    this.adoptTransportGrid(at);
  }

  /** Decoded audio the player keeps at most, in seconds — every render-and-
   * swap decodes a new object; the ones nothing schedules any more go. */
  private static readonly MAX_BUFFER_SECONDS = 1800;
  /** Objects the renderer may hold, as of the last trim. */
  private forgettable = new Set<string>();
  private trimBuffers(keep?: string): void {
    // Scheduled objects, launched ones, and the one a caller just decoded
    // for its own use: with the arrangement alone over the cap, everything
    // else would go, including the object the caller is about to play.
    const inUse = new Set(this.sources.map((s) => s.url));
    for (const v of this.slotVoices.values()) if (v.url !== undefined) inUse.add(v.url);
    if (keep !== undefined) inUse.add(keep);
    let total = 0;
    for (const b of this.buffers.values()) total += b.duration;
    for (const [url, b] of this.buffers) {
      if (total <= ArrangementPlayer.MAX_BUFFER_SECONDS) break;
      if (inUse.has(url)) continue;
      this.buffers.delete(url);
      total -= b.duration;
    }
    for (const [key, v] of [...this.stretched]) if (!this.buffers.has(v.url)) this.stretched.delete(key);
    if (this.renderer) {
      const held = new Set([...this.buffers.keys()]);
      for (const url of this.forgettable) if (!held.has(url)) this.renderer.forget(url);
      this.forgettable = held;
    }
  }

  /**
   * One clip's playback resolved for a caller rendering it on its own
   * context (a lane bounce): the buffer to play — the object, or its
   * stretched copy — and the `start()` arguments and rate that put
   * `durationSec` room seconds of it on the timeline. Null until decoded.
   */
  resolvedFor(src: ClipSource): { buffer: AudioBuffer; offset: number; playFor: number; rate: number } | null {
    const r = this.resolve(src);
    return r ? { buffer: r.buffer, offset: r.offset, playFor: r.playFor, rate: r.rate } : null;
  }

  setMuted(laneId: string, muted: boolean): void {
    if (muted) this.muted.add(laneId);
    else this.muted.delete(laneId);
    this.applyGains();
  }

  setSoloed(laneId: string, soloed: boolean): void {
    if (soloed) this.soloed.add(laneId);
    else this.soloed.delete(laneId);
    this.applyGains();
  }

  clearLaneStates(): void {
    this.muted.clear();
    this.soloed.clear();
    this.applyGains();
  }

  setVolume(v: number): void {
    if (this.master) this.master.gain.value = Math.max(0, Math.min(1, v));
  }

  /** Called from the animation frame; ends or loops the transport at the tail. */
  tick(): void {
    if (!this.playing) return;
    if (this.stopAt !== null && this.position >= this.stopAt) {
      const at = this.stopAt;
      this.stopAt = null;
      this.pause();
      this.startOffset = at;
      this.onEnded?.();
      return;
    }
    const wrap = this.nextWrap;
    if (wrap) {
      // The pass after this one is already sounding from `wrap.at`: move
      // the playhead's bookkeeping over and lay the pass after that.
      if (this.ctx && this.ctx.currentTime >= wrap.at) {
        this.jumpFrom = null;
        this.startOffset = wrap.from;
        this.startedAt = wrap.at;
        const period = wrap.to - wrap.from;
        this.nextWrap = { at: wrap.at + period, from: wrap.from, to: wrap.to };
        this.schedulePass(wrap.from, wrap.to, this.nextWrap.at);
      }
      return;
    }
    // A take can run past the last clip — that is how a Set gets longer — so
    // the tail only ends the transport when nothing is recording.
    if (this.position < this.duration || (this.recorder && !this.loop)) return;
    this.pause();
    this.startOffset = this.duration;
    this.onEnded?.();
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  dispose(): void {
    this.recorder?.stop();
    this.recordStream?.getTracks().forEach((t) => t.stop());
    this.recorder = null;
    this.stopAudition();
    this.stopVoices();
    this.killSlots();
    this.midiFeeds.clear();
    if (this.midiFeedTimer !== null) globalThis.clearInterval(this.midiFeedTimer);
    this.midiFeedTimer = null;
    this.overridden.clear();
    this.buffers.clear();
    this.objectBytes.clear();
    this.peakPreviews.clear();
    this.fromPreview.clear();
    this.prefetching?.abort();
    this.prefetching = null;
    this.peakCache.clear();
    this.summaries.clear();
    this.peakQueue = [];
    this.peakLaterQueue = [];
    this.disposed = true;
    for (const wake of this.peakIdleWaiters.splice(0)) wake();
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.master = null;
    this.masterFaderNodes = null;
    this.masterMeter = null;
    this.lanes.clear();
    // The buses and taps belonged to the closed context; a reused player
    // (StrictMode's second mount) must build fresh ones or sends stay
    // silent for the session (2026-09-08).
    this.returnBuses = [];
    this.sendTaps.clear();
  }

  // ---- internals ----------------------------------------------------------

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      playThroughSilentSwitch();
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      // The Set's master fader and pan, then a soft limiter on the bus: a
      // dozen lanes summing past full scale would otherwise clip hard at
      // the output. Same stages on the mixdown.
      const level = this.ctx.createGain();
      level.gain.value = faderToGain(this.masterFader.volume);
      const pan = createPanner(this.ctx, this.masterFader.pan);
      const chain = buildGraph(this.ctx, this.stagesOf(this.masterStages));
      this.masterFaderNodes = { level, pan, chain };
      const limiter = masterLimiter(this.ctx);
      this.master.connect(chain.input);
      chain.output.connect(level);
      level.connect(pan.input);
      pan.output.connect(limiter);
      limiter.connect(this.ctx.destination);
      this.masterMeter = this.ctx.createAnalyser();
      this.masterMeter.fftSize = METER_WINDOW;
      limiter.connect(this.masterMeter);
    }
    return this.ctx;
  }

  private laneGain(laneId: string): GainNode {
    const ctx = this.ensureContext();
    let nodes = this.lanes.get(laneId);
    if (!nodes) {
      const arrangement = ctx.createGain();
      arrangement.gain.value = this.overridden.has(laneId) ? 0 : 1;
      const gate = ctx.createGain();
      const level = ctx.createGain();
      const pan = createPanner(ctx, CENTER_PAN);
      const meter = ctx.createAnalyser();
      meter.fftSize = METER_WINDOW;
      const direct = ctx.createGain();
      const chain = buildGraph(ctx, this.stagesOf(this.laneStages.get(laneId)));
      arrangement.connect(gate);
      gate.connect(chain.input);
      chain.output.connect(level);
      level.connect(pan.input);
      pan.output.connect(direct);
      direct.connect(this.master!);
      pan.output.connect(meter); // a tap: the meter listens, nothing flows on from it
      nodes = { arrangement, gate, chain, level, pan, direct, meter };
      this.lanes.set(laneId, nodes);
      const stored = this.levels.get(laneId);
      if (stored) this.applyLevel(nodes, stored);
      this.applyGains();
      if (this.returnLevels.length > 0) this.wireSends(laneId);
    }
    return nodes.gate;
  }

  /** Where a TIMELINE voice joins the lane — behind the Session override. */
  private arrangementIn(laneId: string): GainNode {
    this.laneGain(laneId);
    return this.lanes.get(laneId)!.arrangement;
  }

  /** Where this schedule's voices on a lane enter: the lane's arrangement
   * input, through the schedule's own gain. */
  private genIn(laneId: string): GainNode {
    let bus = this.genBuses.get(laneId);
    if (!bus) {
      bus = this.ensureContext().createGain();
      bus.connect(this.arrangementIn(laneId));
      this.genBuses.set(laneId, bus);
    }
    return bus;
  }

  /** Let go of the current schedule's buses: faded out at `at` when given
   * (the voices behind them stopped a fade later by the caller), else cut. */
  private retireBuses(at?: number): void {
    const ctx = this.ensureContext();
    const old = [...this.genBuses.values()];
    this.genBuses = new Map();
    if (at !== undefined) {
      for (const bus of old) {
        bus.gain.setValueAtTime(1, at);
        bus.gain.linearRampToValueAtTime(0, at + RESCHEDULE_XFADE_SEC);
      }
      // Two schedules back is silent by now: let its buses go.
      for (const buses of this.retiredBuses.splice(0, Math.max(0, this.retiredBuses.length - 1))) {
        for (const bus of buses) bus.disconnect();
      }
      this.retiredBuses.push(old);
      return;
    }
    for (const bus of old) bus.disconnect();
    for (const buses of this.retiredBuses) for (const bus of buses) bus.disconnect();
    this.retiredBuses = [];
    void ctx;
  }

  /**
   * The transport follows an edit WITHOUT starting over: the new schedule is
   * laid from where the playhead will be a lead from now and faded in there,
   * while the old one fades out under it (`RESCHEDULE_XFADE_SEC`) and its
   * voices are stopped just after. The position never jumps — a transpose
   * or a warp change is heard as a change in the sound that is playing.
   * Stopped, there is nothing to swap: the next Play reads the new sources.
   */
  async reschedule(): Promise<void> {
    await this.prepare(this.sources);
    if (!this.playing) return;
    const ctx = this.ensureContext();
    const when = ctx.currentTime + TRANSPORT_LEAD_SEC;
    const at = this.position + TRANSPORT_LEAD_SEC; // room seconds at `when`
    const old = this.voices;
    this.voices = [];
    this.retireBuses(when);
    // Until the new run starts, the playhead reads off the old one — a
    // reschedule must not show the position a lead ahead (or behind).
    this.jumpFrom = { offset: this.startOffset, startedAt: this.startedAt, until: when };
    this.startOffset = at >= this.duration ? 0 : at;
    this.startedAt = when;
    this.schedule(when);
    for (const bus of this.genBuses.values()) {
      bus.gain.setValueAtTime(0, when);
      bus.gain.linearRampToValueAtTime(1, when + RESCHEDULE_XFADE_SEC);
    }
    for (const { node } of old) {
      try {
        node.onended = null;
        stopVoice(node, when + RESCHEDULE_XFADE_SEC + 0.005);
      } catch {
        // never started, or already finished
      }
    }
  }

  private applyLevel(nodes: LaneNodes, level: LaneLevel): void {
    nodes.level.gain.value = faderToGain(clampVolume(level.volume));
    nodes.pan.setPan(level.pan);
  }

  // ---- sends and returns ---------------------------------------------------
  // A lane's post-fader signal is tapped into each return bus at its send
  // level; a return bus is level → pan → gate into the master. No effect
  // sits on a return here (Live's do), so a send is heard dry — the levels
  // are the Set's, the sound is the browser's honest approximation.
  private laneSends = new Map<string, number[]>();
  private returnLevels: Array<{ volume: number; pan: number; mute: boolean; solo: boolean }> = [];
  private returnBuses: Array<{ input: GainNode; chain: DeviceGraph; level: GainNode; pan: Panner; gate: GainNode }> = [];
  private sendTaps = new Map<string, GainNode[]>();

  setLaneSends(laneId: string, sends: number[]): void {
    this.laneSends.set(laneId, sends.map(clampVolume));
    if (this.ctx && this.lanes.has(laneId)) this.wireSends(laneId);
  }

  setReturns(returns: Array<{ volume: number; pan: number; mute: boolean; solo: boolean }>): void {
    this.returnLevels = returns.map((r) => ({ volume: clampVolume(r.volume), pan: clampPan(r.pan), mute: r.mute, solo: r.solo }));
    if (!this.ctx) return;
    this.ensureReturnBuses();
    for (const [laneId] of this.lanes) this.wireSends(laneId);
    this.applyGains();
  }

  private ensureReturnBuses(): void {
    const ctx = this.ensureContext();
    while (this.returnBuses.length < this.returnLevels.length) {
      const input = ctx.createGain();
      const level = ctx.createGain();
      const pan = createPanner(ctx, CENTER_PAN);
      const gate = ctx.createGain();
      const chain = buildGraph(ctx, this.stagesOf(this.returnStages[this.returnBuses.length]));
      input.connect(chain.input);
      chain.output.connect(level);
      level.connect(pan.input);
      pan.output.connect(gate);
      gate.connect(this.master!);
      this.returnBuses.push({ input, chain, level, pan, gate });
    }
    const soloing = this.returnLevels.some((r) => r.solo);
    this.returnBuses.forEach((bus, i) => {
      const r = this.returnLevels[i];
      if (!r) {
        bus.gate.gain.value = 0;
        return;
      }
      bus.level.gain.value = faderToGain(r.volume);
      bus.pan.setPan(r.pan);
      bus.gate.gain.value = isAudible(r.mute, r.solo, soloing) ? 1 : 0;
    });
  }

  /** (Re)connect one lane's post-fader taps to the return buses. */
  private wireSends(laneId: string): void {
    const nodes = this.lanes.get(laneId);
    if (!nodes) return;
    this.ensureReturnBuses();
    const ctx = this.ensureContext();
    const sends = this.laneSends.get(laneId) ?? [];
    let taps = this.sendTaps.get(laneId);
    if (!taps) {
      taps = [];
      this.sendTaps.set(laneId, taps);
    }
    for (let i = 0; i < this.returnBuses.length; i++) {
      let tap = taps[i];
      if (!tap) {
        tap = ctx.createGain();
        nodes.pan.output.connect(tap);
        tap.connect(this.returnBuses[i]!.input);
        taps[i] = tap;
      }
      const level = sends[i] ?? 0;
      tap.gain.value = sendToGain(level);
    }
  }

  /** Live's gates: mute wins — a deactivated track is silent even when
   * soloed — and while anything is soloed only soloed lanes pass. A soloed
   * return closes every unsoloed lane's direct path (the sends still flow),
   * as Live's does. */
  /** Whether a lane's gate is open — its timeline material and launched
   * voices audible. Closed by mute/solo and by a clip preview (solo). For
   * the dev inspector and the specs; the UI reads `sessionPlaying` etc. */
  gateOpen(laneId: string): boolean {
    const nodes = this.lanes.get(laneId);
    return nodes !== undefined && nodes.gate.gain.value > 0;
  }

  private applyGains(): void {
    const soloing = this.soloed.size > 0;
    const returnSolo = this.returnLevels.some((r) => r.solo);
    for (const [laneId, nodes] of this.lanes) {
      const audible = isAudible(this.muted.has(laneId), this.soloed.has(laneId), soloing);
      // A preview enters past the gate (`auditionOut`), so closing every
      // gate ducks the mix under it and leaves the preview alone.
      nodes.gate.gain.value = audible && !this.previewSolo ? 1 : 0;
      nodes.direct.gain.value = returnSolo && !this.soloed.has(laneId) ? 0 : 1;
    }
  }

  /** The loop the transport runs in, if any: the region, or the whole
   * arrangement with Loop on. Null plays through. */
  private loopSpan(): { from: number; to: number } | null {
    const range = this.loopRange;
    if (range && range.endSec > range.startSec) return { from: range.startSec, to: range.endSec };
    if (this.loop && this.duration > 0) return { from: 0, to: this.duration };
    return null;
  }

  private schedule(startAt?: number): void {
    const ctx = this.ensureContext();
    const from = this.startOffset;
    // Everything is placed against the moment the transport starts, which is
    // not always now: a start waiting for the Session View's bar line puts
    // the whole arrangement — clicks included — that far ahead.
    const anchor = startAt ?? ctx.currentTime;
    const span = this.loopSpan();
    this.nextWrap = null;
    if (span && from < span.to) {
      // This pass to the loop's end, and the next pass after it — both on
      // the clock now, so the wrap lands on the sample whatever the frame
      // rate (and keeps sounding in a background tab, where frames stop).
      this.schedulePass(from, span.to, anchor);
      const wrapAt = anchor + (span.to - from);
      this.nextWrap = { at: wrapAt, from: span.from, to: span.to };
      this.schedulePass(span.from, span.to, wrapAt);
    } else {
      this.schedulePass(from, Infinity, anchor);
    }
    this.applyGains();
  }

  /**
   * Lay every source and note between `from` and `to` (room seconds) against
   * `anchor`, the context time `from` sounds at. A clip that runs past `to`
   * is stopped there; a note is clipped to it and to its own clip's end.
   *
   * MIDI first: synth voices are the expensive ones to build (a dozen nodes
   * each) and the ones that came in late when they were built last.
   */
  private schedulePass(from: number, to: number, anchor: number): void {
    const passEnd = to === Infinity ? Infinity : anchor + (to - from);

    // MIDI clips: one voice per note, placed at clip start + note start; a
    // note straddling the playhead starts immediately, part-way shortened,
    // and no note outlives its clip or the pass.
    let notes = 0;
    outer: for (const src of this.midi) {
      const clipEnd = src.durationSec > 0 ? src.startSec + src.durationSec : Infinity;
      for (const n of src.notes) {
        const noteStart = src.startSec + n.startSec;
        if (noteStart >= to || noteStart >= clipEnd) continue;
        const noteEnd = Math.min(noteStart + n.durationSec, clipEnd, to);
        if (noteEnd <= from) continue;
        const late = Math.max(0, from - noteStart);
        const playFor = noteEnd - Math.max(noteStart, from);
        if (playFor <= 0) continue;
        if (++notes > ArrangementPlayer.MAX_ARRANGEMENT_VOICES) break outer;
        const when = anchor + Math.max(0, noteStart - from);
        const node = this.startTone(
          { ...n, durationSec: playFor, startSec: n.startSec + late },
          when,
          this.genIn(src.laneId),
          this.patchFor(src.laneId),
        );
        this.voices.push({ node });
      }
    }

    for (const src of this.sources) this.layAudio(src, from, to, anchor, passEnd);
    if (this.metronome && this.tempo > 0) this.scheduleClicks(from, anchor, to);
  }

  /** One audio clip's voice in a pass, if it has decoded and falls inside it. */
  private layAudio(src: ClipSource, from: number, to: number, anchor: number, passEnd: number): void {
    if (!this.buffers.has(src.url)) return;
    const ctx = this.ensureContext();
    const declared = this.lengthOf(src);
    const endSec = src.startSec + declared;
    if (endSec <= from || src.startSec >= to) return; // outside the pass

    // A clip straddling the start position begins immediately, part-way in.
    const late = Math.max(0, from - src.startSec);
    const when = anchor + Math.max(0, src.startSec - from);
    const resolved = this.resolve(src, late);
    if (!resolved) return;
    const { buffer, offset, playFor, rate, outSec } = resolved;

    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.playbackRate.value = rate;
    const outEnd = Math.min(outSec, passEnd - when);
    node.connect(declick(ctx, this.genIn(src.laneId), when, outEnd));
    node.start(when, offset, playFor);
    if (passEnd !== Infinity && when + outSec > passEnd) node.stop(passEnd);
    this.voices.push({ node });
  }

  /** Cap on clicks scheduled per play — at 120 BPM that is 33 minutes. */
  private static readonly MAX_CLICKS = 4000;

  /**
   * Click track: one short burst per beat from `from` to the end (or the loop
   * region's end), the downbeat of every bar higher. Scheduled on the
   * context clock like everything else, so it never drifts against the clips.
   */
  private scheduleClicks(from: number, anchor?: number, to = Infinity): void {
    const ctx = this.ensureContext();
    const at = anchor ?? ctx.currentTime;
    // One click per beat OF THE METER (an eighth at 6/8), the bar's first higher.
    const beat = (60 / this.tempo) * beatUnit(this.meter);
    const end = Math.min(to, this.duration);
    let index = Math.ceil(from / beat - 1e-6);
    let count = 0;
    for (; index * beat < end && count < ArrangementPlayer.MAX_CLICKS; index++, count++) {
      this.click(at + (index * beat - from), index % this.meter.numerator === 0);
    }
  }

  /** One metronome tick at a context time. Transport clicks join `voices`
   * (Stop silences them); a count-in's don't — nothing else is playing yet. */
  private click(when: number, accent: boolean, into: "transport" | "session" | null = "transport"): OscillatorNode {
    const ctx = this.ensureContext();
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = accent ? 1600 : 1000;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(accent ? 0.25 : 0.15, when + 0.002);
    env.gain.exponentialRampToValueAtTime(0.001, when + 0.04);
    osc.connect(env);
    env.connect(this.master!);
    osc.start(when);
    osc.stop(when + 0.05);
    osc.addEventListener("ended", () => env.disconnect());
    if (into === "transport") this.voices.push({ node: osc });
    return osc;
  }

  private startTone(note: MidiNoteEvent, when: number, out: AudioNode, patch: SynthPatch = DEFAULT_PATCH): OscillatorNode {
    return scheduleTone(this.ensureContext(), note, when, out, patch);
  }

  private stopVoices(): void {
    this.nextWrap = null;
    if (this.ctx) this.retireBuses();
    for (const { node } of this.voices) {
      try {
        node.onended = null;
        stopVoice(node);
        node.disconnect();
      } catch {
        // never started, or already finished
      }
    }
    this.voices = [];
  }
}

/**
 * A gain stage with a few milliseconds of ramp at a voice's start and end,
 * on either context — a clip cut mid-waveform (any clip that doesn't start
 * on a zero crossing, i.e. most) otherwise clicks at both edges. Voices too
 * short for two ramps pass straight through.
 */
export function declick(ctx: BaseAudioContext, out: AudioNode, when: number, durationSec: number): AudioNode {
  if (durationSec < DECLICK_SEC * 4) return out;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(1, when + DECLICK_SEC);
  g.gain.setValueAtTime(1, when + durationSec - DECLICK_SEC);
  g.gain.linearRampToValueAtTime(0, when + durationSec);
  g.connect(out);
  return g;
}

/** A mixer pan stage with Live's law (`panGains`): constant power, 0 dB
 * per side at centre, +3 dB on the near side at a hard pan. Each channel is
 * scaled by its own side's gain — a balance control on a stereo signal,
 * never a fold — and a mono signal is up-mixed to both sides first, so it
 * pans like Live's mono tracks do. */
export interface Panner {
  input: AudioNode;
  output: AudioNode;
  setPan(pan: number): void;
}

export function createPanner(ctx: BaseAudioContext, pan: number): Panner {
  const input = ctx.createGain();
  input.channelCount = 2;
  input.channelCountMode = "explicit";
  input.channelInterpretation = "speakers";
  const split = ctx.createChannelSplitter(2);
  const left = ctx.createGain();
  const right = ctx.createGain();
  const output = ctx.createChannelMerger(2);
  input.connect(split);
  split.connect(left, 0);
  split.connect(right, 1);
  left.connect(output, 0, 0);
  right.connect(output, 0, 1);
  const setPan = (p: number): void => {
    const g = panGains(p);
    left.gain.value = g.left;
    right.gain.value = g.right;
  };
  setPan(pan);
  return { input, output, setPan };
}

/** The bus limiter: a gentle compressor that only works above −3 dBFS, so
 * a normal mix passes untouched and a hot one folds instead of clipping. */
export function masterLimiter(ctx: BaseAudioContext): DynamicsCompressorNode {
  const c = ctx.createDynamicsCompressor();
  c.threshold.value = -3;
  c.knee.value = 3;
  c.ratio.value = 12;
  c.attack.value = 0.003;
  c.release.value = 0.12;
  return c;
}

/** The next bar line at or after a transport position, in seconds. A
 * position within a hair of a bar line IS that bar line — launching "on
 * the bar" from the bar must not wait a whole bar. */
export function nextBarStart(positionSec: number, bpm: number, beatsPerBar = 4): number {
  if (!(bpm > 0)) return positionSec;
  const bar = (beatsPerBar * 60) / bpm;
  const bars = positionSec / bar;
  const rounded = Math.round(bars);
  if (Math.abs(bars - rounded) * bar < 0.005) return rounded * bar;
  return Math.ceil(bars) * bar;
}

/** Beats → seconds at a given tempo. Guards a zero/absent tempo. */
export function beatsToSeconds(beats: number, bpm: number): number {
  return bpm > 0 ? (beats * 60) / bpm : 0;
}

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00.0";
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
}

/**
 * iOS gives Web Audio the "ambient" audio session, which the ring/silent
 * switch mutes, while an <audio> element (the seed page's player) plays on:
 * a phone in silent mode heard seeds but not the room. "playback" is what a
 * music app asks for (Safari 16.4+'s Audio Session API); elsewhere a no-op.
 */
function playThroughSilentSwitch(): void {
  const session = (globalThis.navigator as (Navigator & { audioSession?: { type: string } }) | undefined)?.audioSession;
  if (session && session.type !== "playback") session.type = "playback";
}

/**
 * The gain that brings a region of a buffer to the audition level: its RMS
 * over every channel, read at a stride (a 10-minute object is still one
 * pass of a few thousand samples), bounded so silence is not blown up and
 * a slammed loop is not buried.
 */
export function levelGain(buffer: AudioBuffer, offsetSec: number, lengthSec: number, target = AUDITION_LEVEL_RMS): number {
  const from = Math.max(0, Math.floor(offsetSec * buffer.sampleRate));
  const to = Math.min(buffer.length, Math.ceil((offsetSec + lengthSec) * buffer.sampleRate));
  if (to <= from) return 1;
  const stride = Math.max(1, Math.floor((to - from) / 8192));
  let sum = 0;
  let n = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = from; i < to; i += stride) {
      const v = data[i]!;
      sum += v * v;
      n++;
    }
  }
  if (n === 0) return 1;
  const rms = Math.sqrt(sum / n);
  if (!(rms > 1e-5)) return 1;
  return Math.min(AUDITION_LEVEL_MAX_GAIN, Math.max(1 / AUDITION_LEVEL_MAX_GAIN, target / rms));
}
